import { Clipboard } from '@angular/cdk/clipboard';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, Renderer2, TemplateRef, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FetchPolicy } from '@apollo/client';
import {GraphQLModule, dayjs} from '@karve.it/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryRef } from 'apollo-angular';
import { GraphQLFormattedError } from 'graphql';
import {
  AvailableZoneFragment,
  BaseDynamicReportFragment,
  BaseOptionFragment,
  ColumnFormat,
  CreateDynamicReportsExportTokenGQL,
  CustomColumnName,
  DataOnlyGQL,
  DeleteDynamicReportGQL,
  DynamicReportOptionsGQL,
  DynamicReportParametersInput,
  DynamicReportsGQL,
  Jobs_ClosedReasons,
  FullUserFragment,
  HourFilter,
  ListExpensesGQL,
  ListExpensesQueryVariables,
  ListTaxesGQL,
  PreviewReportFilter,
  PreviewReportGQL,
  PreviewReportQuery,
  PreviewReportQueryVariables,
  Token,
  TransactionStage,
  UpdateDynamicReportGQL,
  UserPageUsersGQL,
  ZoneDir,
} from 'graphql.generated';

import { unparse } from 'papaparse';
import { LazyLoadEvent, MenuItem, TreeNode } from 'primeng/api';
import { TableColumnReorderEvent } from 'primeng/table';
import { BehaviorSubject, combineLatest, firstValueFrom, interval, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { AvailableZonesService } from 'src/app/services/available-zones.service';
import { generateBranches } from 'src/app/utilities/zones.util';
import { SubSink } from 'subsink';

import { environment } from '../../../environments/environment';
import { AppMainComponent } from '../../app.main.component';
import { PeriodService } from '../../dashboard/period.service';
import { DATE_PARAM_FORMAT, DEFAULT_HOW_HEARD_DROPDOWN_OPTIONS, JOB_EVENT_STATUSES, JOB_STAGES, JOB_STATES, MAX_32_BIT_INT, eventTypeInfoMap, pagination } from '../../global.constants';
import { strToTitleCase } from '../../js';
import { periodToString } from '../../reports/report.utils';
import { AdvancedPeriodOptions } from '../../reports/reports.constants';
import { BrandingService } from '../../services/branding.service';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { FreyaMutateService } from '../../services/freya-mutate.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { TimezoneHelperService } from '../../services/timezone-helper.service';
import { Filter } from '../../shared/add-filter/add-filter.component';
import { ParamTypes, QueryParamsService } from '../../shared/query-params.service';
import { buildSourceTree, SOURCE_TIER_SEPARATOR } from '../../shared/source-tiers';
import { TemplateTypeDirective } from '../../shared/template-type.directive';
import { BrandingActions, createConfigSelector } from '../../state/branding.store';
import { currentTimeSeconds, dateToUnixSeconds, normalizeDateRangeParam } from '../../time';
import {
  getReportTimeoutDetail,
  isReportTimeoutError,
  isReportTimeoutGraphqlError,
  REPORT_TIMEOUT_PREVIEW_ADVICE,
} from '../../utilities/errors.util';
import { WatchQueryHelper } from '../../utilities/watchQueryHelper';

/**
 * The timeout toast carries remediation advice, so it needs longer on screen than the 5s default
 * used for short failure messages.
 */
const REPORT_TIMEOUT_TOAST_LIFE_MS = 15000;

// Step 1: Define an interface for the form structure
interface ReportParamsFormControls {
  period: FormControl<AdvancedPeriodOptions>;
  customPeriod: FormControl<Date[]>;
  periodType: FormControl<string>;
  jobHasCompletedEventOfType: FormControl<string[]>;
  jobStage: FormControl<string[]>;
  jobState: FormControl<string[]>;
  jobSources: FormControl<string[]>;
  jobSourceTiers: FormControl<string[]>;
  jobFranchises: FormControl<string[]>;
  jobAreas: FormControl<string[]>;
  expenseIds: FormControl<string[]>;
  noSource: FormControl<boolean>;
  jobOrigins: FormControl<string[]>;
  noOrigin: FormControl<boolean>;
  jobTypes: FormControl<string[]>;
  jobEstimateMethods: FormControl<string[]>;
  jobEstimators: FormControl<string[]>;
  jobSalesAgents: FormControl<string[]>;
  attentionExplanations: FormControl<string[]>;
  noAttentionExplanation: FormControl<boolean>;
  closedReason: FormControl<string>;
  excludeClosedReasons: FormControl<string[]>;
  utmSources: FormControl<string[]>;
  noUtmSource: FormControl<boolean>;
  utmMedia: FormControl<string[]>;
  noUtmMedium: FormControl<boolean>;
  utmContents: FormControl<string[]>;
  noUtmContent: FormControl<boolean>;
  utmTerms: FormControl<string[]>;
  noUtmTerm: FormControl<boolean>;
  utmCampaigns: FormControl<string[]>;
  noUtmCampaign: FormControl<boolean>;
  currency: FormControl<string>;
  currencySource: FormControl<string>;
  eventStatuses: FormControl<string[]>;
  eventSharedStatuses: FormControl<string[]>;
  eventTypes: FormControl<string[]>;
  bookedOnDaysOfWeek: FormControl<number[]>;
  bookedFromHour: FormControl<Date | null>;
  bookedUntilHour: FormControl<Date | null>;
  invoiceStatuses: FormControl<string[]>;
  invoiceBalances: FormControl<string[]>;
  transactionStages: FormControl<string[]>;
  excludeDamages: FormControl<boolean>;
  excludeZeroTotalEvents: FormControl<boolean>;
  countRevenueFromEventsWithStatus: FormControl<string[]>;
  groupBy: FormControl<string>;
  sort: FormControl<string>;
  dataColumns: FormControl<string[]>;
  aggregationColumns: FormControl<string[]>;
  distanceMin: FormControl<number | null>;
  distanceMax: FormControl<number | null>;
  realizedBy: FormControl<Date>;
  callClosedReasons: FormControl<string[]>;
  callModes: FormControl<string[]>;
  callDispositions: FormControl<string[]>;
  callFromNumber: FormControl<string>;
  callToNumber: FormControl<string>;
  callOrigins: FormControl<string[]>;
  callStatuses: FormControl<string[]>;
  callCallStatuses: FormControl<string[]>;
  callToCities: FormControl<string[]>;
  callToStates: FormControl<string[]>;
  callToZips: FormControl<string[]>;
  callToCountries: FormControl<string[]>;
  callFromCities: FormControl<string[]>;
  callFromStates: FormControl<string[]>;
  callFromZips: FormControl<string[]>;
  callFromCountries: FormControl<string[]>;
  callStirVerstat: FormControl<string>;
  callTdInteractionId: FormControl<string>;
  callTwilioApiVersion: FormControl<string>;
  callTwilioAccountSids: FormControl<string[]>;
  callTwilioCallbackSources: FormControl<string[]>;
  callCommentAuthors: FormControl<string[]>;
  callCommentIsSystem: FormControl<boolean>;
  callCommentAddedByIds: FormControl<string[]>;
  callCommentIsAutodialer: FormControl<boolean>;
  callCommentExternalMessageIds: FormControl<string[]>;
  callCommentProviders: FormControl<string[]>;
  callCommentRingGroups: FormControl<string[]>;
  taxIds: FormControl<string[]>;
  limit: FormControl<number>;
  periodBucket: FormControl<string>;
}

type ReportFormControlName = keyof ReportParamsFormControls;

export type ReportParamsForm = FormGroup<ReportParamsFormControls>;
type ReportParamsFormValue = ReturnType<ReportParamsForm['getRawValue']>;

interface EventTypeFilterOption {
  option: string;
  label: string;
  legacy?: boolean;
}

@Component({
  selector: 'app-preview-dynamic-report',
  templateUrl: './preview-dynamic-report.component.html',
  styleUrls: ['./preview-dynamic-report.component.scss']
})
export class PreviewDynamicReportComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChildren(TemplateTypeDirective) filterTemplates: QueryList<TemplateTypeDirective>;

  pagination = pagination;

  reportQH: WatchQueryHelper = {
    skip: 0,
    loading: true,
    total: 100,
  };

  lastRefresh: number;

  reportQueryRef: QueryRef<PreviewReportQuery, PreviewReportQueryVariables>;

  existingReport: BaseDynamicReportFragment;

  aggregationHeaders: string[];

  aggregationRows: string[][];

  dataHeaders: string[];

  dataRows: string[][];

  breadcrumb: MenuItem[];

  home: MenuItem;

  groupByOpts: BaseOptionFragment[];

  defaultGroupBy = 'area';

  groupBy = this.defaultGroupBy;

  defaultPeriod: AdvancedPeriodOptions = 'This Month';

  defaultLimit = 10;

  aggregationColumnOptions: BaseOptionFragment[];

  reportParamsForm: ReportParamsForm;

  reportParamsFormDefault: ReportParamsFormValue;

  periods = this.periodService.getPeriodNameValues({
    includeCustom: true,
    defaultPeriod: this.defaultPeriod,
  }).filter((p) => ![
    'All Time',
    'Future',
  ].includes(p.name));

  periodTypeOptions: BaseOptionFragment[];

  periodTypeDescriptions = {
    eventStart: 'The date the event was scheduled to start',
    eventBookedAt: 'The date the event was booked',
  };

  lastValidCustomPeriod: Date[];

  dataColumnOptions: { option: string; label: string }[];

  currencyOptions = [ 'CAD', 'USD'] as const;

  currencySourceOptions = [ 'charge', 'job', 'zone' ] as const;

  periodBucketOptions = [
    'hour', 'day', 'week', 'month', 'quarter', 'year',
    'hourOfDay', 'dayOfWeek', 'dayOfMonth', 'monthOfYear', 'quarterOfYear',
  ] as const;

  jobStages = Object.values(JOB_STAGES).map((s) => ({
    option: s.name,
    label: strToTitleCase(s.name)
  }));

  jobStates = JOB_STATES.map((s) => ({
    option: s,
    label: strToTitleCase(s),
  }));

  eventStatuses = JOB_EVENT_STATUSES.map((s) => ({
    option: s,
    label: strToTitleCase(s),
  }));

  eventSharedStatuses = [
    { option: 'shared', label: 'Shared' },
    { option: 'not-shared', label: 'Not Shared' },
  ];

  eventTypeOptions: EventTypeFilterOption[] = [];

  daysOfTheWeek = [
    { option: 1, label: 'Monday' },
    { option: 2, label: 'Tuesday' },
    { option: 3, label: 'Wednesday' },
    { option: 4, label: 'Thursday' },
    { option: 5, label: 'Friday' },
    { option: 6, label: 'Saturday' },
    { option: 7, label: 'Sunday' },
  ];

  invoiceStatuses = [
    {
      option: 'draft',
      label: 'Draft',
    },
    {
      option: 'finalized',
      label: 'Finalized',
    },
  ];

  transactionStages = [
    {
      option: 'pending',
      label: 'Pending',
    },
    {
      option: 'paid',
      label: 'Paid',
    },
    {
      option: 'cancelled',
      label: 'Cancelled',
    },
  ];

  invoiceBalances = [
    {
      option: 'paid',
      label: 'Paid',
    },
    {
      option: 'outstanding',
      label: 'Outstanding',
    },
  ];

  callModes = [
    {
      option: 'Inbound Call',
      label: 'Inbound Call',
    },
    {
      option: 'Outbound Call',
      label: 'Outbound Call',
    },
  ];

  subs = new SubSink();

  filters: Filter[];

  controlsWithDefaultFilters: ReportFormControlName[] = [ 'jobState', 'eventStatuses', 'countRevenueFromEventsWithStatus' ];

  chips: {
    ctrlName: ReportFormControlName;
    name: string;
    value: string | string[];
  }[] = [];

  /**
   * Tree of configured `jobs.howHeardOptions` paths, used by the tiered-source
   * (subtree) filter. See docs/tiered-job-sources.md.
   */
  sourceTierTree: TreeNode[] = [];

  /** Currently-checked nodes in the tiered-source tree (PrimeNG checkbox model). */
  selectedSourceTierNodes: TreeNode[] = [];

  controlDisplayNames: Partial<Record<ReportFormControlName, string>> = {
    jobStage: 'Job Stage',
    jobState: 'Job State',
    jobSources: 'Source (exact match)',
    jobSourceTiers: 'Source (tiered)',
    jobFranchises: 'Business Unit',
    jobAreas: 'Area',
    expenseIds: 'Expenses',
    taxIds: 'Taxes',
    noSource: 'Missing Source',
    jobOrigins: 'Job Origin',
    noOrigin: 'Missing Origin',
    jobTypes: 'Job Type',
    jobEstimateMethods: 'Estimate Method',
    jobEstimators: 'Estimator',
    jobSalesAgents: 'Sales Agent',
    attentionExplanations: 'Attention Explanation',
    noAttentionExplanation: 'Does Not Require Attention',
    closedReason: 'Closure Reason',
    excludeClosedReasons: 'Exclude Closure Reasons',
    utmSources: 'UTM Source',
    noUtmSource: 'Missing UTM Source',
    utmMedia: 'UTM Media',
    noUtmMedium: 'Missing UTM Medium',
    utmContents: 'UTM Content',
    noUtmContent: 'Missing UTM Content',
    utmTerms: 'UTM Term',
    noUtmTerm: 'Missing UTM term',
    utmCampaigns: 'UTM Campaign',
    noUtmCampaign: 'Missing UTM Campaign',
    jobHasCompletedEventOfType: 'Has Completed Event of Type',
    eventStatuses: 'Event Status',
    eventSharedStatuses: 'Shared',
    eventTypes: 'Event Type',
    bookedOnDaysOfWeek: 'Booked On Day of The Week',
    bookedFromHour: 'Booked No Earlier Than',
    bookedUntilHour: 'Booked No Later Than',
    invoiceStatuses: 'Invoice Status',
    invoiceBalances: 'Invoice Balance',
    transactionStages: 'Transaction Status',
    excludeDamages: 'Exclude Damages',
    excludeZeroTotalEvents: 'Exclude Zero-Total Events',
    countRevenueFromEventsWithStatus: 'Count Revenue From Events With Status',
    currency: 'Currency',
    currencySource: 'Currency Source',
    distanceMin: 'Distance Min',
    distanceMax: 'Distance Max',
    realizedBy: 'Realized By',
    callClosedReasons: 'Call Closed Reason',
    callModes: 'Call Mode',
    callDispositions: 'Call Disposition',
    callFromNumber: 'Call From Number',
    callToNumber: 'Call To Number',
    callOrigins: 'Call Origin',
    callStatuses: 'Call Status',
    callCallStatuses: 'Call Status',
    callToCities: 'Call To City',
    callToStates: 'Call To State',
    callToZips: 'Call To Zip',
    callToCountries: 'Call To Country',
    callFromCities: 'Call From City',
    callFromStates: 'Call From State',
    callFromZips: 'Call From Zip',
    callFromCountries: 'Call From Country',
    callStirVerstat: 'Call STIR/Verstat',
    callTdInteractionId: 'Call TD Interaction ID',
    callTwilioApiVersion: 'Call Twilio API Version',
    callTwilioAccountSids: 'Call Twilio Account SID',
    callTwilioCallbackSources: 'Call Twilio Callback Source',
    callCommentAuthors: 'Call Comment Author',
    callCommentIsSystem: 'Call Comment Is System',
    callCommentAddedByIds: 'Call Comment Added By',
    callCommentIsAutodialer: 'Call Comment Is Autodialer',
    callCommentExternalMessageIds: 'Call Comment External Message ID',
    callCommentProviders: 'Call Comment Provider',
    callCommentRingGroups: 'Call Comment Ring Group',
  };

  dataActions: MenuItem[] = [];

  existingReportActions: MenuItem[] = [
    {
      label: 'Save As',
      icon: 'pi pi-save',
      command: () => this.openCreateDynamicReportDialog(),
    },
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: () => this.openUpdateDynamicReportDialog(),
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => this.deleteDynamicReport(),
    },
  ];

  actionsLoading = false;

  skipAggregations = false;

  exportDialogOpen = false;

  exportFormula: string;

  exportSettingsForm = new FormGroup({
    includeSummary: new FormControl(true),
    includeData: new FormControl(true),
  }, { validators: this.atLeastOneTrue });

  exportTokenRefreshing = false;

  refreshEnabled = false;

  refreshEnabledMinSecs = 60;

  reset$ = new Subject<void>();

  hasChanges = false;

  saving = false;

  groupByWithDuplicateRevenue: Record<string, boolean> = {
    crewMember: true,
    discount: true,
    jobCrewMember: true,
    eventCrewMember: true,
  };

  _userRecord: Record<string, FullUserFragment> = {};

  userRecordChecked = false;

  calculatorDialogOpen = false;

  advanceSettingsDialogOpen = false;

  calculatorColumnForm: FormGroup;

  taxColumnDialogOpen = false;

  taxColumnForm: FormGroup;

  columnFormats: Map<string, string> = new Map();

  customColumnNames: Map<string, string> = new Map();

  customColumnNamesDialogOpen = false;

  // Inputs bound to the Custom Column Names dialog; keyed by column id.
  customColumnNamesDialogValue: Record<string, string> = {};

  // Mirrors the backend pattern in CUSTOM_COLUMN_NAME_PATTERN — keep them in sync.
  readonly customColumnNamePattern = /^[A-Za-z0-9 _\-().,&%$+/]+$/;

  readonly customColumnNameMaxLength = 100;

  franchiseOptions$ = combineLatest([
    this.availableZones.availableZones,
    this.graphqlModule.zone.asObservable(),
  ]).pipe(
    map(([ availableZones, currentZoneId ]) => {

      const currentZone = availableZones?.find((z) => z.id === currentZoneId);

      if (!currentZone) { return []; }

      if (currentZone.type === 'franchise') {
        return [ currentZone ];
      }

      const zoneTree = generateBranches(availableZones);

      const stack = [ currentZone.id ];
      const seen = new Set<string>();
      const descendants: AvailableZoneFragment[] = [];

      while (stack.length) {
        const zoneId = stack.pop()!;
        const children = zoneTree[zoneId].children;
        for (const child of children) {
          if (seen.has(child.zone.id)) continue;
          seen.add(child.zone.id);
          descendants.push(child.zone);
          stack.push(child.zone.id);
        }
      }

      return descendants.filter((d) => d.type === 'franchise');
    }),
    tap((zones) => zones.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))),
    map((zones) => zones.map((z) => ({ label: z.name, option: z.id }))),
  );

  franchiseOptions: MenuItem[] = [];

  areaOptions$ = combineLatest([
    this.availableZones.availableZones,
    this.graphqlModule.zone.asObservable(),
  ]).pipe(
    map(([ availableZones, currentZoneId ]) => {

      const currentZone = availableZones?.find((z) => z.id === currentZoneId);

      if (!currentZone) { return []; }

      if (currentZone.type === 'area') {
        return [ currentZone ];
      }

      const zoneTree = generateBranches(availableZones);

      const stack = [ currentZone.id ];
      const seen = new Set<string>();
      const descendants: AvailableZoneFragment[] = [];

      while (stack.length) {
        const zoneId = stack.pop()!;
        const children = zoneTree[zoneId].children;
        for (const child of children) {
          if (seen.has(child.zone.id)) continue;
          seen.add(child.zone.id);
          descendants.push(child.zone);
          stack.push(child.zone.id);
        }
      }

      return descendants.filter((d) => d.type === 'area');
    }),
    tap((zones) => zones.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))),
    map((zones) => zones.map((z) => ({ label: z.name, option: z.id }))),
  );

  areaOptions: MenuItem[] = [];

  private _expensesQuery = new BehaviorSubject('');

  public expensesQuery$ = this._expensesQuery.asObservable();

  public expenseOptions$ = this.expensesQuery$.pipe(
    map((query): ListExpensesQueryVariables => ({ filter: { expenseNameSearch: query } })),
    switchMap((vars) => this.listExpensesGQL.fetch(vars)),
    map((res) => res.data.expenses.expenses.map((e) => ({
      option: e.id,
      label: e.name,
    }))),
  );

  public expenseOptions: { option: string; label: string; }[] = [];

  // Fetch all taxes once and derive both filter options (by id/name) and column options (by unique type)
  public allTaxes$ = this.listTaxesGQL.fetch({ filter: {} }).pipe(
    map((res) => {
      const taxes = res.data.taxes.taxes;

      // Options for the tax filter (by id/name)
      const taxOptions = taxes.map((t) => ({
        option: t.id,
        label: t.name,
      }));

      // Options for the tax column dropdown (unique types)
      const types = new Set<string>();
      for (const tax of taxes) {
        if (tax.type) {
          types.add(tax.type);
        }
      }
      const taxTypeOptions = Array.from(types).sort().map((type) => ({
        option: type,
        label: type,
      }));

      return { taxOptions, taxTypeOptions };
    }),
  );

  public taxOptions: { option: string; label: string; }[] = [];

  public taxTypeOptions: { option: string; label: string; }[] = [];

  constructor(
    private previewReportGQL: PreviewReportGQL,
    private dataOnlyGQL: DataOnlyGQL,
    private periodService: PeriodService,
    private clipboard: Clipboard,
    private localNotify: FreyaNotificationsService,
    private brandingService: BrandingService,
    private appMain: AppMainComponent,
    private queryParams: QueryParamsService,
    private router: Router,
    private route: ActivatedRoute,
    public freyaHelper: FreyaHelperService,
    private freyaMutate: FreyaMutateService,
    private dynamicReportsGQL: DynamicReportsGQL,
    private detailsHelper: DetailsHelperService,
    private actions: Actions,
    private dynamicReportOptionsGQL: DynamicReportOptionsGQL,
    private timezoneHelper: TimezoneHelperService,
    private createDynamicReportExportTokenGQL: CreateDynamicReportsExportTokenGQL,
    private updateDyanmicReportGQL: UpdateDynamicReportGQL,
    private deleteDynamicReportGQL: DeleteDynamicReportGQL,
    private el: ElementRef,
    private renderer: Renderer2,
    private userPageUsersGQL: UserPageUsersGQL,
    private availableZones: AvailableZonesService,
    private graphqlModule: GraphQLModule,
    private listExpensesGQL: ListExpensesGQL,
    private listTaxesGQL: ListTaxesGQL,
    private store: Store,
  ) { }

  /**
   * Requirement: `#7020` - closure-reason filter chips show each reason's title from the zone's
   * `jobs.closedReasons` config instead of its raw id.
   */
  closedReasons$ = this.store.select(createConfigSelector('JOBS_CLOSEDREASONS'));
  closedReasons: Jobs_ClosedReasons[] = [];

  ngOnInit(): void {
    this.startTimer();
    this.fetchOptions().then(() => {
      this.initForm();
      this.watchEventTypeOptionChanges();
      this.setDataActions(this.reportParamsForm.get('groupBy')?.value);
      this.initCalculatorColumnForm();
      this.initTaxColumnForm();
      this.watchPeriodChanges();
      this.watchCustomPeriodchanges();
      this.watchGroupByChanges();
      this.refreshOnFilterChanges();
      this.syncFilterChipsToForm();
      this.syncFilterToQueryParams();
      this.loadNewlyCreatedReports();
      this.resetSortOnColumnChanges();
    });

    this.setBreadcrumb();

    this.subs.sink = this.exportSettingsForm.valueChanges
      .pipe(
        filter(() => this.exportDialogOpen && this.exportSettingsForm.valid),
        debounceTime(400),
      )
      .subscribe(() => {
        this.refreshExportToken();
      });

    this.subs.sink = this.franchiseOptions$.subscribe((options) => {

      this.franchiseOptions = options;

      if (this.chips.length > 0) {
        this.setFilterChips();
      }
    });

    this.subs.sink = this.areaOptions$.subscribe((options) => {

      this.areaOptions = options;

      if (this.chips.length > 0) {
        this.setFilterChips();
      }
    });

    this.subs.sink = this.expenseOptions$.subscribe((options) => {

      this.expenseOptions = options;

      if (this.chips.length > 0) {
        this.setFilterChips();
      }
    });

    this.subs.sink = this.allTaxes$.subscribe(({ taxOptions, taxTypeOptions }) => {
      this.taxOptions = taxOptions;
      this.taxTypeOptions = taxTypeOptions;

      if (this.chips.length > 0) {
        this.setFilterChips();
      }
    });

  }

  ngAfterViewInit(): void {
    this.filterTemplates.changes
      .subscribe(() => {
        const templates = this.filterTemplates.toArray();
        const jobState = templates.find((t) => t.appTemplateType === 'jobState').templateRef;
        const jobStage = templates.find((t) => t.appTemplateType === 'jobStage').templateRef;
        const jobSources = templates.find((t) => t.appTemplateType === 'jobSources').templateRef;
        const jobFranchises = templates.find((t) => t.appTemplateType === 'jobFranchises').templateRef;
        const jobAreas = templates.find((t) => t.appTemplateType === 'jobAreas').templateRef;
        const expenseIds = templates.find((t) => t.appTemplateType === 'expenseIds').templateRef;
        const taxIds = templates.find((t) => t.appTemplateType === 'taxIds').templateRef;
        const jobOrigins = templates.find((t) => t.appTemplateType === 'jobOrigins').templateRef;
        const jobTypes = templates.find((t) => t.appTemplateType === 'jobTypes').templateRef;
        const jobEstimateMethods = templates.find((t) => t.appTemplateType === 'jobEstimateMethods').templateRef;
        const jobEstimators = templates.find((t) => t.appTemplateType === 'jobEstimators').templateRef;
        const jobSalesAgents = templates.find((t) => t.appTemplateType === 'jobSalesAgents').templateRef;
        const attentionExplanation = templates.find((t) => t.appTemplateType === 'attentionExplanation').templateRef;
        const closedReason = templates.find((t) => t.appTemplateType === 'closedReason').templateRef;
        const utmSource = templates.find((t) => t.appTemplateType === 'utmSource').templateRef;
        const utmMedium = templates.find((t) => t.appTemplateType === 'utmMedium').templateRef;
        const utmContents = templates.find((t) => t.appTemplateType === 'utmContents').templateRef;
        const utmTerms = templates.find((t) => t.appTemplateType === 'utmTerms').templateRef;
        const utmCampaign = templates.find((t) => t.appTemplateType === 'utmCampaign').templateRef;
        const eventStatus = templates.find((t) => t.appTemplateType === 'eventStatus').templateRef;
        const eventType = templates.find((t) => t.appTemplateType === 'eventType').templateRef;
        const eventSharedStatus = templates.find((t) => t.appTemplateType === 'eventSharedStatus').templateRef;
        const invoiceStatus = templates.find((t) => t.appTemplateType === 'invoiceStatus').templateRef;
        const invoiceBalance = templates.find((t) => t.appTemplateType === 'invoiceBalance').templateRef;
        const transactionStage = templates.find((t) => t.appTemplateType === 'transactionStage').templateRef;
        const bookedOnDaysOfWeek = templates.find((t) => t.appTemplateType === 'bookedOnDaysOfWeek').templateRef;
        const bookedFromHour = templates.find((t) => t.appTemplateType === 'bookedFromHour').templateRef;
        const bookedUntilHour = templates.find((t) => t.appTemplateType === 'bookedUntilHour').templateRef;
        const currency = templates.find((t) => t.appTemplateType === 'currency').templateRef;
        const jobHasCompletedEventOfType = templates.find((t) => t.appTemplateType === 'jobHasCompletedEventOfType').templateRef;
        const distanceMin = templates.find((t) => t.appTemplateType === 'distanceMin').templateRef;
        const distanceMax = templates.find((t) => t.appTemplateType === 'distanceMax').templateRef;
        const realizedBy = templates.find((t) => t.appTemplateType === 'realizedBy').templateRef;
        const excludeDamages = templates.find((t) => t.appTemplateType === 'excludeDamages').templateRef;
        const excludeZeroTotalEvents = templates.find((t) => t.appTemplateType === 'excludeZeroTotalEvents').templateRef;
        const countRevenueFromEventsWithStatus = templates
          .find((t) => t.appTemplateType === 'countRevenueFromEventsWithStatus').templateRef;
        const callClosedReasons = templates.find((t) => t.appTemplateType === 'callClosedReasons').templateRef;
        const callModes = templates.find((t) => t.appTemplateType === 'callModes').templateRef;
        const callDispositions = templates.find((t) => t.appTemplateType === 'callDispositions').templateRef;
        const callFromNumber = templates.find((t) => t.appTemplateType === 'callFromNumber').templateRef;
        const callToNumber = templates.find((t) => t.appTemplateType === 'callToNumber').templateRef;
        const callOrigins = templates.find((t) => t.appTemplateType === 'callOrigins').templateRef;
        const callStatuses = templates.find((t) => t.appTemplateType === 'callStatuses').templateRef;
        const callCallStatuses = templates.find((t) => t.appTemplateType === 'callCallStatuses').templateRef;
        const callToCities = templates.find((t) => t.appTemplateType === 'callToCities').templateRef;
        const callToStates = templates.find((t) => t.appTemplateType === 'callToStates').templateRef;
        const callToZips = templates.find((t) => t.appTemplateType === 'callToZips').templateRef;
        const callToCountries = templates.find((t) => t.appTemplateType === 'callToCountries').templateRef;
        const callFromCities = templates.find((t) => t.appTemplateType === 'callFromCities').templateRef;
        const callFromStates = templates.find((t) => t.appTemplateType === 'callFromStates').templateRef;
        const callFromZips = templates.find((t) => t.appTemplateType === 'callFromZips').templateRef;
        const callFromCountries = templates.find((t) => t.appTemplateType === 'callFromCountries').templateRef;
        const callStirVerstat = templates.find((t) => t.appTemplateType === 'callStirVerstat').templateRef;
        const callTdInteractionId = templates.find((t) => t.appTemplateType === 'callTdInteractionId').templateRef;
        const callTwilioApiVersion = templates.find((t) => t.appTemplateType === 'callTwilioApiVersion').templateRef;
        const callTwilioAccountSids = templates.find((t) => t.appTemplateType === 'callTwilioAccountSids').templateRef;
        const callTwilioCallbackSources = templates.find((t) => t.appTemplateType === 'callTwilioCallbackSources').templateRef;
        const callCommentAuthors = templates.find((t) => t.appTemplateType === 'callCommentAuthors').templateRef;
        const callCommentIsSystem = templates.find((t) => t.appTemplateType === 'callCommentIsSystem').templateRef;
        const callCommentAddedByIds = templates.find((t) => t.appTemplateType === 'callCommentAddedByIds').templateRef;
        const callCommentIsAutodialer = templates.find((t) => t.appTemplateType === 'callCommentIsAutodialer').templateRef;
        const callCommentExternalMessageIds = templates.find((t) => t.appTemplateType === 'callCommentExternalMessageIds').templateRef;
        const callCommentProviders = templates.find((t) => t.appTemplateType === 'callCommentProviders').templateRef;
        const callCommentRingGroups = templates.find((t) => t.appTemplateType === 'callCommentRingGroups').templateRef;

        this.setFilters({
          jobState,
          jobStage,
          jobSources,
          jobFranchises,
          jobAreas,
          jobOrigins,
          jobTypes,
          jobEstimateMethods,
          jobEstimators,
          jobSalesAgents,
          attentionExplanation,
          closedReason,
          utmSource,
          utmMedium,
          utmContents,
          utmTerms,
          utmCampaign,
          eventStatus,
          eventType,
          eventSharedStatus,
          expenseIds,
          taxIds,
          invoiceStatus,
          invoiceBalance,
          transactionStage,
          bookedOnDaysOfWeek,
          bookedFromHour,
          bookedUntilHour,
          currency,
          jobHasCompletedEventOfType,
          distanceMin,
          distanceMax,
          realizedBy,
          excludeDamages,
          excludeZeroTotalEvents,
          countRevenueFromEventsWithStatus,
          callClosedReasons,
          callModes,
          callDispositions,
          callFromNumber,
          callToNumber,
          callOrigins,
          callStatuses,
          callCallStatuses,
          callToCities,
          callToStates,
          callToZips,
          callToCountries,
          callFromCities,
          callFromStates,
          callFromZips,
          callFromCountries,
          callStirVerstat,
          callTdInteractionId,
          callTwilioApiVersion,
          callTwilioAccountSids,
          callTwilioCallbackSources,
          callCommentAuthors,
          callCommentIsSystem,
          callCommentAddedByIds,
          callCommentIsAutodialer,
          callCommentExternalMessageIds,
          callCommentProviders,
          callCommentRingGroups,
        });
        this.appMain.cd.detectChanges();
      });
  }

  get userRecord() {
    return this._userRecord;
  }

  set userRecord(record: typeof this._userRecord) {
    this._userRecord = record;
    this.setFilterChips();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  retrieveMoreRows(event: LazyLoadEvent) {
    this.reportQH.skip = event.first;

    const limitCtrl = this.reportParamsForm.get('limit');
    if (limitCtrl && limitCtrl.value !== event.rows) {
      limitCtrl.setValue(event.rows);
    }

    this.previewReport(event.first > 0);
  }

  fetchOptions(): Promise<void> {
    const obs = this.dynamicReportOptionsGQL.fetch({ groupBy: this.defaultGroupBy })
    return firstValueFrom(obs).then((val) => {
      this.groupByOpts = val.data.groupByOptions;
      this.periodTypeOptions = val.data.periodTypeOptions;
      this.aggregationColumnOptions = val.data.aggregationColumns;
      this.dataColumnOptions = val.data.dataColumns;
    });
  }

  initForm() {
    this.reportParamsForm = new FormGroup({
      period: new FormControl(this.defaultPeriod),
      currency: new FormControl(undefined),
      currencySource: new FormControl('charge'),
      customPeriod: new FormControl({
        value: this.periodService.getPeriodAsDateArray(this.defaultPeriod),
        disabled: true,
      }),
      periodType: new FormControl('eventEnd'),
      jobStage: new FormControl([ ]),
      jobState: new FormControl([ 'open', 'closed', 'archived' ]),
      jobSources: new FormControl([ ]),
      jobSourceTiers: new FormControl([ ]),
      jobFranchises: new FormControl([ ]),
      jobAreas: new FormControl([ ]),
      expenseIds: new FormControl([ ]),
      taxIds: new FormControl([ ]),
      noSource: new FormControl(false),
      jobOrigins: new FormControl([ ]),
      noOrigin: new FormControl(false),
      jobTypes: new FormControl([ ]),
      jobEstimateMethods: new FormControl([ ]),
      jobEstimators: new FormControl([ ]),
      jobSalesAgents: new FormControl([ ]),
      attentionExplanations: new FormControl([ ]),
      noAttentionExplanation: new FormControl(false),
      closedReason: new FormControl(''),
      excludeClosedReasons: new FormControl([ ]),
      utmSources: new FormControl([ ]),
      noUtmSource: new FormControl(false),
      utmMedia: new FormControl([ ]),
      noUtmMedium: new FormControl(false),
      utmContents: new FormControl([ ]),
      noUtmContent: new FormControl(false),
      utmTerms: new FormControl([ ]),
      noUtmTerm: new FormControl(false),
      utmCampaigns: new FormControl([ ]),
      noUtmCampaign: new FormControl(false),
      jobHasCompletedEventOfType: new FormControl([]),
      eventStatuses: new FormControl([]),
      eventSharedStatuses: new FormControl([]),
      eventTypes: new FormControl([ ]),
      bookedOnDaysOfWeek: new FormControl([ ]),
      bookedFromHour: new FormControl(null),
      bookedUntilHour: new FormControl(null),
      invoiceStatuses: new FormControl([ ]),
      invoiceBalances: new FormControl([ ]),
      transactionStages: new FormControl([ ]),
      excludeDamages: new FormControl(false),
      excludeZeroTotalEvents: new FormControl(false),
      countRevenueFromEventsWithStatus: new FormControl([ 'required', 'pending', 'booked', 'confirmed', 'completed' ]),
      groupBy: new FormControl(this.defaultGroupBy),
      sort: new FormControl(undefined),
      dataColumns: new FormControl(this.getDefaultColumns(this.defaultGroupBy)),
      aggregationColumns: new FormControl(this.aggregationColumnOptions.map((o) => o.option)),
      distanceMin: new FormControl(null),
      distanceMax: new FormControl(null),
      realizedBy: new FormControl(undefined),
      callClosedReasons: new FormControl([]),
      callModes: new FormControl([]),
      callDispositions: new FormControl([]),
      callFromNumber: new FormControl(''),
      callToNumber: new FormControl(''),
      callOrigins: new FormControl([]),
      callStatuses: new FormControl([]),
      callCallStatuses: new FormControl([]),
      callToCities: new FormControl([]),
      callToStates: new FormControl([]),
      callToZips: new FormControl([]),
      callToCountries: new FormControl([]),
      callFromCities: new FormControl([]),
      callFromStates: new FormControl([]),
      callFromZips: new FormControl([]),
      callFromCountries: new FormControl([]),
      callStirVerstat: new FormControl(''),
      callTdInteractionId: new FormControl(''),
      callTwilioApiVersion: new FormControl(''),
      callTwilioAccountSids: new FormControl([]),
      callTwilioCallbackSources: new FormControl([]),
      callCommentAuthors: new FormControl([]),
      callCommentIsSystem: new FormControl(false),
      callCommentAddedByIds: new FormControl([]),
      callCommentIsAutodialer: new FormControl(false),
      callCommentExternalMessageIds: new FormControl([]),
      callCommentProviders: new FormControl([]),
      callCommentRingGroups: new FormControl([]),
      limit: new FormControl(this.defaultLimit),
      periodBucket: new FormControl('month'),
    });

    this.refreshEventTypeOptions();

    this.reportParamsFormDefault = this.reportParamsForm.getRawValue();

    this.lastValidCustomPeriod = this.reportParamsForm.value.customPeriod;
  }

  watchEventTypeOptionChanges() {
    const eventTypesCtrl = this.reportParamsForm.get('eventTypes');
    if (eventTypesCtrl) {
      this.subs.sink = eventTypesCtrl.valueChanges.subscribe(() => {
        this.refreshEventTypeOptions();
      });
    }

    const completedEventTypesCtrl = this.reportParamsForm.get('jobHasCompletedEventOfType');
    if (completedEventTypesCtrl) {
      this.subs.sink = completedEventTypesCtrl.valueChanges.subscribe(() => {
        this.refreshEventTypeOptions();
      });
    }

    this.subs.sink = this.actions
      .pipe(ofType(BrandingActions.brandingUpdated))
      .subscribe(() => {
        this.refreshEventTypeOptions();
      });

    // Build the tiered-source tree from the configured how-heard options.
    this.subs.sink = this.store
      .select(createConfigSelector('JOBS_HOWHEARDOPTIONS', DEFAULT_HOW_HEARD_DROPDOWN_OPTIONS))
      // eslint-disable-next-line @ngrx/no-store-subscription -- pre-existing subscription, unrelated to this fix
      .subscribe((options) => {
        this.sourceTierTree = buildSourceTree(options || []);
        this.syncSourceTierNodesFromForm();
      });

    // Re-render the chips when the closure reasons load or change (e.g. after a zone switch), so a
    // chip drawn before the config arrived does not keep showing the raw id.
    this.subs.sink = this.closedReasons$
      // eslint-disable-next-line @ngrx/no-store-subscription
      .subscribe((reasons) => {
        this.closedReasons = reasons ?? [];
        if (this.chips.length > 0) {
          this.setFilterChips();
        }
      });
  }

  /**
   * User (un)checked a node in the tiered-source tree. Store the minimal set of
   * top-level checked paths on the `jobSourceTiers` control: the backend matches
   * the whole subtree of each path, so a descendant of an already-checked path
   * is redundant. See docs/tiered-job-sources.md.
   */
  onSourceTierSelectionChange(nodes: TreeNode[]) {
    this.selectedSourceTierNodes = nodes || [];

    const keys = this.selectedSourceTierNodes
      .filter((node) => !node.partialSelected)
      .map((node) => node.key as string)
      .filter(Boolean);

    const minimal = keys.filter((key) =>
      !keys.some((other) => other !== key && key.startsWith(other + SOURCE_TIER_SEPARATOR)),
    );

    this.reportParamsForm.get('jobSourceTiers')?.setValue(minimal);
  }

  /**
   * Reflect the `jobSourceTiers` form value (e.g. loaded from a saved report)
   * into the tree's checkbox selection so the checked state is visible.
   */
  syncSourceTierNodesFromForm() {
    const paths: string[] = this.reportParamsForm?.get('jobSourceTiers')?.value || [];

    if (!paths.length || !this.sourceTierTree.length) {
      this.selectedSourceTierNodes = [];
      return;
    }

    const selected: TreeNode[] = [];

    const collect = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        const key = node.key as string;
        const inSubtree = paths.some((p) => key === p || key.startsWith(p + SOURCE_TIER_SEPARATOR));
        if (inSubtree) { selected.push(node); }
        if (node.children?.length) { collect(node.children); }
      }
    };

    collect(this.sourceTierTree);
    this.selectedSourceTierNodes = selected;
  }

  refreshEventTypeOptions() {
    const optionRecord = new Map<string, EventTypeFilterOption>();

    for (const eventType of Object.values(eventTypeInfoMap)) {
      const value = eventType?.value;
      if (!value || optionRecord.has(value)) { continue; }

      optionRecord.set(value, {
        option: value,
        label: eventType.name || strToTitleCase(value),
      });
    }

    const selectedValues = new Set<string>();
    const formValue = this.reportParamsForm?.getRawValue();
    for (const value of formValue?.eventTypes || []) {
      if (value) {
        selectedValues.add(value);
      }
    }
    for (const value of formValue?.jobHasCompletedEventOfType || []) {
      if (value) {
        selectedValues.add(value);
      }
    }

    for (const value of selectedValues) {
      if (optionRecord.has(value)) { continue; }

      optionRecord.set(value, {
        option: value,
        label: `${strToTitleCase(value)} (Legacy)`,
        legacy: true,
      });
    }

    this.eventTypeOptions = Array.from(optionRecord.values())
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }

  initCalculatorColumnForm() {
    this.calculatorColumnForm = new FormGroup({
      columnKey: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[^\s]+$/)  // No spaces allowed
      ]),
      format: new FormControl(''),
    });
  }

  initTaxColumnForm() {
    this.taxColumnForm = new FormGroup({
      taxType: new FormControl('', [
        Validators.required,
      ]),
    });
  }

  createDuplicateKeyValidator(existingKeys: string[]) {
    return (control: FormControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const prefixedKey = `calculator_${control.value}`;

      if (existingKeys.includes(prefixedKey)) {
        return { duplicateKey: true };
      }

      return null;
    };
  }

  createDuplicateTaxValidator(existingColumns: string[]) {
    return (control: FormControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const prefixedKey = `taxTotal_${control.value.replace(/\s+/g, '_')}`;

      if (existingColumns.includes(prefixedKey)) {
        return { duplicateKey: true };
      }

      return null;
    };
  }

  previewReport(skipAggregations = false) {

    this.skipAggregations = skipAggregations;

    if (this.reportQueryRef) {
      this.reportQueryRef.refetch(this.getQueryVariables());
      return;
    }

    this.reportQueryRef = this.previewReportGQL.watch(this.getQueryVariables(), {
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    });;

    this.subs.sink = this.reportQueryRef.valueChanges.subscribe((val) => {
      this.reportQH.loading = val.loading;

      if (val.loading) { return; }

      if (val.errors?.length) {
        this.notifyPreviewError(val.errors);
      }

      if (!val.data) { return; }

      if (!this.skipAggregations) {
        this.aggregationHeaders = val.data.previewReport.aggregationData.headers;
        this.aggregationRows = val.data.previewReport.aggregationData.rows;
        this.reportQH.total = val.data.previewReport.total;
      }

      this.dataHeaders = val.data.previewReport.data.headers;

      this.dataRows = val.data.previewReport.data.rows;

      this.setDataColumnOptions(val.data.dataColumns)

      this.groupBy = val.data.previewReport.groupBy;

      this.lastRefresh = currentTimeSeconds();

      this.restartTimer();
    }, (err) => {
      // A timed-out report can also arrive here as the ingress's bare 504, which never reaches
      // `val.errors` because it fails the request rather than returning GraphQL errors. Such an
      // error carries no `graphQLErrors`, so the timeout verdict has to be passed in explicitly —
      // otherwise the helper's GraphQL-only check misses it and shows the generic toast.
      if (isReportTimeoutError(err)) {
        this.notifyPreviewError(err.graphQLErrors, true);
      } else {
        this.localNotify.apolloError('Failed to preview report', err);
      }

      this.reportQH.loading = false;
    });
  }

  // Requirement: a report stopped by the server-side timeout must tell the user what happened and
  // how to get a result, rather than surfacing the raw error. Other failures keep the generic toast.
  private notifyPreviewError(
    errors: readonly GraphQLFormattedError[] | undefined,
    isTimeout = isReportTimeoutGraphqlError(errors),
  ) {

    if (!isTimeout) {
      this.localNotify.error('Failed to preview report', errors?.[0]?.message);
      return;
    }

    this.localNotify.error(
      'Report timed out',
      getReportTimeoutDetail(errors, REPORT_TIMEOUT_PREVIEW_ADVICE),
      REPORT_TIMEOUT_TOAST_LIFE_MS,
    );
  }

  getQueryVariables(): PreviewReportQueryVariables {

    const {
      groupBy,
      sort,
      period,
      periodType,
      jobStage,
      jobState,
      jobSources,
      jobSourceTiers,
      jobFranchises,
      jobAreas,
      noSource,
      jobOrigins,
      noOrigin,
      jobTypes,
      jobEstimateMethods,
      jobEstimators,
      jobSalesAgents,
      attentionExplanations,
      noAttentionExplanation,
      closedReason,
      excludeClosedReasons,
      utmSources,
      noUtmSource,
      utmMedia,
      noUtmMedium,
      utmContents,
      noUtmContent,
      utmTerms,
      noUtmTerm,
      utmCampaigns,
      noUtmCampaign,
      jobHasCompletedEventOfType,
      eventStatuses,
      eventSharedStatuses,
      eventTypes,
      bookedOnDaysOfWeek,
      bookedFromHour,
      bookedUntilHour,
      dataColumns,
      aggregationColumns,
      invoiceStatuses,
      invoiceBalances,
      transactionStages,
      currency,
      currencySource,
      distanceMin,
      distanceMax,
      realizedBy,
      excludeDamages,
      excludeZeroTotalEvents,
      countRevenueFromEventsWithStatus,
      expenseIds,
      taxIds,
      callClosedReasons,
      callModes,
      callDispositions,
      callFromNumber,
      callToNumber,
      callOrigins,
      callStatuses,
      callCallStatuses,
      callToCities,
      callToStates,
      callToZips,
      callToCountries,
      callFromCities,
      callFromStates,
      callFromZips,
      callFromCountries,
      callStirVerstat,
      callTdInteractionId,
      callTwilioApiVersion,
      callTwilioAccountSids,
      callTwilioCallbackSources,
      callCommentAuthors,
      callCommentIsSystem,
      callCommentAddedByIds,
      callCommentIsAutodialer,
      callCommentExternalMessageIds,
      callCommentProviders,
      callCommentRingGroups,
      periodBucket,
    } = this.reportParamsForm.getRawValue();

    let bookedFromHourFilter: HourFilter;
    let bookedUntilHourFilter: HourFilter;

    if (bookedFromHour) {
      bookedFromHourFilter = {
        hour: bookedFromHour.getHours(),
        minute: bookedFromHour.getMinutes(),
      };
    }

    if (bookedUntilHour) {
      bookedUntilHourFilter = {
        hour: bookedUntilHour.getHours(),
        minute: bookedUntilHour.getMinutes(),
      };
    }

    let { customPeriod } = this.reportParamsForm.getRawValue();

    if (!customPeriod) {
      customPeriod = this.periodService.getPeriodAsDateArray(period);
    }

    const [ startDate, endDate ] = customPeriod;

    const timezone = this.brandingService.currentBranding.value?.timezone || environment.defaultTimezone;

    // Extract the calendar date (YYYY-MM-DD) from the Date object, then parse it
    // in the target timezone. This ensures we get the correct unix timestamp for
    // "start of day" and "end of day" in the branding timezone, regardless of
    // the user's local timezone.
    const startDateStr = dayjs(startDate).format('YYYY-MM-DD');
    const endDateStr = dayjs(endDate).format('YYYY-MM-DD');

    const startUnix = dayjs.tz(startDateStr, timezone).startOf('day').unix();

    const endUnix = dayjs.tz(endDateStr, timezone).endOf('day').unix();

    const normalizedDataColumns = dataColumns ?? [];

    const columnFormats = Array.from(this.columnFormats)
      .filter(([ column ]) => normalizedDataColumns.includes(column))
      .map(([ column, format ]) => ({ column, format }));

    const customColumnNames = Array.from(this.customColumnNames)
      .filter(([ column ]) => normalizedDataColumns.includes(column))
      .map(([ column, name ]) => ({ column, name }));

    const queryVars: PreviewReportQueryVariables = {
      skip: this.reportQH.skip,
      limit: this.reportParamsForm.value.limit ?? this.defaultLimit,
      periodType,
      groupBy,
      sort: sort ?? undefined,
      dataColumns: normalizedDataColumns,
      aggregationColumns: aggregationColumns ?? [],
      start: startUnix,
      end: Math.min(endUnix, MAX_32_BIT_INT),
      filter: {
        job: {
          getJobsOfStage: jobStage,
          getJobsOfState: jobState,
          currency,
          hasCompletedEventOfType: jobHasCompletedEventOfType,
          sources: jobSources,
          sourceTiers: jobSourceTiers,
          noSource,
          franchiseIds: jobFranchises,
          areaIds: jobAreas,
          origins: jobOrigins,
          noOrigin,
          types: jobTypes,
          estimateMethods: jobEstimateMethods,
          estimatorIds: jobEstimators,
          utmSources,
          noUtmSource,
          utmContents,
          utmMedia,
          noUtmMedium,
          noUtmContents: noUtmContent,
          utmTerms,
          noUtmTerm,
          utmCampaigns,
          noUtmCampaign,
          salesAgentIds: jobSalesAgents,
          attentionExplanations,
          noAttentionExplanation,
          closedReason,
          excludeClosedReasons,
        },
        event: {
          getEventsOfStatus: eventStatuses,
          getEventsOfType: eventTypes,
          getEventsOfSharedStatus: eventSharedStatuses,
          bookedOnDaysOfWeek,
          bookedFromHour: bookedFromHourFilter,
          bookedUntilHour: bookedUntilHourFilter,
          distanceMin,
          distanceMax,
          excludeZeroTotal: excludeZeroTotalEvents,
        },
        invoice: {
          getInvoiceOfStatus: invoiceStatuses,
          balance: invoiceBalances,
        },
        transaction: {
          stages: transactionStages as TransactionStage[],
        },
        charge: {
          excludeDamages,
          eventStatuses: countRevenueFromEventsWithStatus,
          taxIds,
        },
        expense: {
          ids: expenseIds,
        },
        call: {
          closedReasons: callClosedReasons,
          modes: callModes,
          dispositions: callDispositions,
          fromNumber: callFromNumber,
          toNumber: callToNumber,
          origins: callOrigins,
          statuses: callStatuses,
          callStatuses: callCallStatuses,
          toCities: callToCities,
          toStates: callToStates,
          toZips: callToZips,
          toCountries: callToCountries,
          fromCities: callFromCities,
          fromStates: callFromStates,
          fromZips: callFromZips,
          fromCountries: callFromCountries,
          stirVerstat: callStirVerstat,
          tdInteractionId: callTdInteractionId,
          twilioApiVersion: callTwilioApiVersion,
          twilioAccountSids: callTwilioAccountSids,
          twilioCallbackSources: callTwilioCallbackSources,
        },
        callRootComment: {
          commentAuthors: callCommentAuthors,
          isSystem: callCommentIsSystem,
          addedByIds: callCommentAddedByIds,
          isAutodialer: callCommentIsAutodialer,
          externalMessageIds: callCommentExternalMessageIds,
          providers: callCommentProviders,
          ringGroups: callCommentRingGroups,
        },
      },
      columnParams: {
        realizedBy: realizedBy ? dateToUnixSeconds(realizedBy) : null,
        periodBucket: periodBucket || undefined,
      },
      columnFormats,
      customColumnNames,
      performance: this.skipAggregations ? 'skip-count' : 'all',
      currencySource,
    };

    return queryVars;
  }

  watchPeriodChanges() {
    this.subs.sink = this.reportParamsForm.controls.period.valueChanges
      .subscribe((period: AdvancedPeriodOptions) => this.setCustomPeriodEnabled(period));
  }

  setCustomPeriodEnabled(period: AdvancedPeriodOptions) {
    if (period === 'Custom') {
      this.reportParamsForm.controls.customPeriod.enable();
    } else {
      this.reportParamsForm.controls.customPeriod.disable({ emitEvent: false });
      this.reportParamsForm.controls.customPeriod.setValue(this.periodService.getPeriodAsDateArray(period), { emitEvent: false });
      this.refreshReport();
    }
  }

  watchCustomPeriodchanges() {
    this.subs.sink = this.reportParamsForm.controls.customPeriod.valueChanges
    .pipe(debounceTime(200))
    .subscribe((period: Date[]) => {

      period = period.filter(Boolean);

      if (period.length < 2) { return; }

      this.lastValidCustomPeriod = period;

      this.refreshReport();
    });
  }

  resetCustomPeriod() {
    const customPeriodCtrl = this.reportParamsForm.get('customPeriod');
    const customPeriod = customPeriodCtrl.value.filter(Boolean);

    if (customPeriod.length < 2) {
      customPeriodCtrl.setValue(this.lastValidCustomPeriod, { emitEvent: false });
    }
  }

  watchGroupByChanges() {

    const sortCtrl = this.reportParamsForm.get('sort');
    const columnsCtrl = this.reportParamsForm.get('dataColumns');

    this.subs.sink = this.reportParamsForm.controls.groupBy.valueChanges.subscribe((groupBy) => {
      sortCtrl.setValue(undefined, { emitEvent: false });
      columnsCtrl.setValue(this.getDefaultColumns(groupBy), { emitEvent: false });
      this.setDataActions(groupBy);
    });
  }

  refreshOnFilterChanges() {

    const ctrlDebounceTimeMap: Partial<Record<ReportFormControlName, number>> = {
      groupBy: 0,
      dataColumns: 750,
      aggregationColumns: 750,
      periodType: 0,
      sort: 0,
      jobState: 750,
      jobStage: 750,
      jobSources: 0,
      jobFranchises: 0,
      jobAreas: 0,
      noSource: 0,
      jobOrigins: 0,
      noOrigin: 0,
      jobTypes: 0,
      jobEstimateMethods: 0,
      jobEstimators: 0,
      jobSalesAgents: 0,
      attentionExplanations: 0,
      noAttentionExplanation: 0,
      closedReason: 2500,
      excludeClosedReasons: 0,
      utmSources: 0,
      noUtmSource: 0,
      utmMedia: 0,
      noUtmMedium: 0,
      utmContents: 0,
      noUtmContent: 0,
      utmTerms: 0,
      noUtmTerm: 0,
      utmCampaigns: 0,
      noUtmCampaign: 0,
      jobHasCompletedEventOfType: 750,
      eventStatuses: 750,
      eventTypes: 750,
      eventSharedStatuses: 750,
      bookedOnDaysOfWeek: 750,
      bookedFromHour: 750,
      bookedUntilHour: 750,
      distanceMin: 2500,
      distanceMax: 2500,
      invoiceStatuses: 750,
      invoiceBalances: 750,
      transactionStages: 750,
      currency: 750,
      currencySource: 0,
      realizedBy: 750,
      excludeDamages: 0,
      excludeZeroTotalEvents: 0,
      countRevenueFromEventsWithStatus: 750,
      expenseIds: 0,
      taxIds: 0,
      callClosedReasons: 0,
      callModes: 750,
      callDispositions: 0,
      callFromNumber: 2500,
      callToNumber: 2500,
      callOrigins: 0,
      callStatuses: 0,
      callCallStatuses: 0,
      callToCities: 0,
      callToStates: 0,
      callToZips: 0,
      callToCountries: 0,
      callFromCities: 0,
      callFromStates: 0,
      callFromZips: 0,
      callFromCountries: 0,
      callStirVerstat: 2500,
      callTdInteractionId: 2500,
      callTwilioApiVersion: 2500,
      callTwilioAccountSids: 0,
      callTwilioCallbackSources: 0,
      callCommentAuthors: 0,
      callCommentIsSystem: 0,
      callCommentAddedByIds: 0,
      callCommentIsAutodialer: 0,
      callCommentExternalMessageIds: 0,
      callCommentProviders: 0,
      callCommentRingGroups: 0,
      periodBucket: 0,
    };

    const changesToWatch: Observable<any>[] = [];

    for (const ctrlName of Object.keys(ctrlDebounceTimeMap)) {
      if (ctrlDebounceTimeMap[ctrlName] > 0) {
        changesToWatch.push(this.reportParamsForm.get(ctrlName).valueChanges.pipe(debounceTime(ctrlDebounceTimeMap[ctrlName])));
      } else {
        changesToWatch.push(this.reportParamsForm.get(ctrlName).valueChanges);
      }
    }

    this.subs.sink = merge(...changesToWatch)
      .pipe(debounceTime(200))
      .subscribe(() => this.refreshReport());
  }

  refreshReport() {
    const { dataColumns } = this.getQueryVariables();
    if (!dataColumns?.length) { return; }
    this.reportQH.skip = 0;
    this.previewReport();
  }

  setFilters(input: {
    jobStage: TemplateRef<any>;
    jobState: TemplateRef<any>;
    jobSources: TemplateRef<any>;
    jobFranchises: TemplateRef<any>;
    jobAreas: TemplateRef<any>;
    jobOrigins: TemplateRef<any>;
    jobTypes: TemplateRef<any>;
    jobEstimateMethods: TemplateRef<any>;
    jobEstimators: TemplateRef<any>;
    jobSalesAgents: TemplateRef<any>;
    attentionExplanation: TemplateRef<any>;
    closedReason: TemplateRef<any>;
    utmSource: TemplateRef<any>;
    utmMedium: TemplateRef<any>;
    utmContents: TemplateRef<any>;
    utmTerms: TemplateRef<any>;
    utmCampaign: TemplateRef<any>;
    eventStatus: TemplateRef<any>;
    eventType: TemplateRef<any>;
    eventSharedStatus: TemplateRef<any>;
    expenseIds: TemplateRef<any>;
    taxIds: TemplateRef<any>;
    invoiceStatus: TemplateRef<any>;
    invoiceBalance: TemplateRef<any>;
    transactionStage: TemplateRef<any>;
    bookedOnDaysOfWeek: TemplateRef<any>;
    bookedFromHour: TemplateRef<any>;
    bookedUntilHour: TemplateRef<any>;
    currency: TemplateRef<any>;
    jobHasCompletedEventOfType: TemplateRef<any>;
    distanceMin: TemplateRef<any>;
    distanceMax: TemplateRef<any>;
    realizedBy: TemplateRef<any>;
    excludeDamages: TemplateRef<any>;
    excludeZeroTotalEvents: TemplateRef<any>;
    countRevenueFromEventsWithStatus: TemplateRef<any>;
    callClosedReasons: TemplateRef<any>;
    callModes: TemplateRef<any>;
    callDispositions: TemplateRef<any>;
    callFromNumber: TemplateRef<any>;
    callToNumber: TemplateRef<any>;
    callOrigins: TemplateRef<any>;
    callStatuses: TemplateRef<any>;
    callCallStatuses: TemplateRef<any>;
    callToCities: TemplateRef<any>;
    callToStates: TemplateRef<any>;
    callToZips: TemplateRef<any>;
    callToCountries: TemplateRef<any>;
    callFromCities: TemplateRef<any>;
    callFromStates: TemplateRef<any>;
    callFromZips: TemplateRef<any>;
    callFromCountries: TemplateRef<any>;
    callStirVerstat: TemplateRef<any>;
    callTdInteractionId: TemplateRef<any>;
    callTwilioApiVersion: TemplateRef<any>;
    callTwilioAccountSids: TemplateRef<any>;
    callTwilioCallbackSources: TemplateRef<any>;
    callCommentAuthors: TemplateRef<any>;
    callCommentIsSystem: TemplateRef<any>;
    callCommentAddedByIds: TemplateRef<any>;
    callCommentIsAutodialer: TemplateRef<any>;
    callCommentExternalMessageIds: TemplateRef<any>;
    callCommentProviders: TemplateRef<any>;
    callCommentRingGroups: TemplateRef<any>;
  }) {

    const {
      jobStage,
      jobState,
      jobSources,
      jobFranchises,
      jobAreas,
      jobOrigins,
      jobTypes,
      jobEstimateMethods,
      jobEstimators,
      jobSalesAgents,
      attentionExplanation,
      closedReason,
      utmSource,
      utmMedium,
      utmContents,
      utmTerms,
      utmCampaign,
      eventStatus,
      eventType,
      eventSharedStatus,
      invoiceStatus,
      invoiceBalance,
      transactionStage,
      bookedOnDaysOfWeek,
      bookedFromHour,
      bookedUntilHour,
      currency,
      jobHasCompletedEventOfType,
      distanceMin,
      distanceMax,
      realizedBy,
      excludeDamages,
      excludeZeroTotalEvents,
      countRevenueFromEventsWithStatus,
      expenseIds,
      taxIds,
      callClosedReasons,
      callModes,
      callDispositions,
      callFromNumber,
      callToNumber,
      callOrigins,
      callStatuses,
      callCallStatuses,
      callToCities,
      callToStates,
      callToZips,
      callToCountries,
      callFromCities,
      callFromStates,
      callFromZips,
      callFromCountries,
      callStirVerstat,
      callTdInteractionId,
      callTwilioApiVersion,
      callTwilioAccountSids,
      callTwilioCallbackSources,
      callCommentAuthors,
      callCommentIsSystem,
      callCommentAddedByIds,
      callCommentIsAutodialer,
      callCommentExternalMessageIds,
      callCommentProviders,
      callCommentRingGroups,
    } = input;

    this.filters = [
      {
        label: 'Job',
        items: [
          {
            label: 'Stage',
            filter: jobStage,
          },
          {
            label: 'State',
            filter: jobState,
          },
          {
            label: 'Has Completed Event of Type',
            filter: jobHasCompletedEventOfType,
          },
          {
            label: 'Currency',
            filter: currency,
          },
          {
            label: 'Source',
            filter: jobSources,
          },
          {
            label: 'Origin',
            filter: jobOrigins,
          },
          {
            label: 'Type',
            filter: jobTypes,
          },
          {
            label: 'Business Unit',
            filter: jobFranchises,
          },
          {
            label: 'Area',
            filter: jobAreas,
          },
          {
            label: 'Estimate Method',
            filter: jobEstimateMethods,
          },
          {
            label: 'Estimator',
            filter: jobEstimators,
          },
          {
            label: 'Sales Agent',
            filter: jobSalesAgents,
          },
          {
            label: 'Needs Attention',
            filter: attentionExplanation,
          },
          {
            label: 'Job Closure Reason',
            filter: closedReason,
          },
        ],
      },
      {
        label: 'Event',
        items: [
          {
            label: 'Status',
            filter: eventStatus,
          },
          {
            label: 'Type',
            filter: eventType,
          },
          {
            label: 'Shared',
            filter: eventSharedStatus,
          },
          {
            label: 'Booked On Day of The Week',
            filter: bookedOnDaysOfWeek,
          },
          {
            label: 'Booked No Earlier Than',
            filter: bookedFromHour,
          },
          {
            label: 'Booked No Later Than',
            filter: bookedUntilHour,
          },
          {
            label: 'Distance Min',
            filter: distanceMin,
          },
          {
            label: 'Distance Max',
            filter: distanceMax,
          },
          {
            label: 'Exclude Zero-Total Events',
            filter: excludeZeroTotalEvents,
          },
        ],
      },
      {
        label: 'Invoice',
        items: [
          {
            label: 'Status',
            filter: invoiceStatus,
          },
          {
            label: 'Balance',
            filter: invoiceBalance,
          }
        ],
      },
      {
        label: 'Transaction',
        items: [
          {
            label: 'Status',
            filter: transactionStage,
          },
        ],
      },
      {
        label: 'Revenue',
        items: [
          {
            label: 'Realized By',
            filter: realizedBy,
          },
          {
            label: 'Exclude Damages',
            filter: excludeDamages,
          },
          {
            label: 'Event Status',
            filter: countRevenueFromEventsWithStatus,
          }
        ],
      },
      {
        label: 'Expense',
        items: [
          {
            label: 'Expense',
            filter: expenseIds,
          }
        ],
      },
      {
        label: 'Tax',
        items: [
          {
            label: 'Tax',
            filter: taxIds,
          }
        ],
      },
      {
        label: 'Call',
        items: [
          {
            label: 'Mode',
            filter: callModes,
          },
          {
            label: 'From',
            items: [
              {
                label: 'Number',
                filter: callFromNumber,
              },
              {
                label: 'City',
                filter: callFromCities,
              },
              {
                label: 'State',
                filter: callFromStates,
              },
              {
                label: 'Zip',
                filter: callFromZips,
              },
              {
                label: 'Country',
                filter: callFromCountries,
              },
            ],
          },
          {
            label: 'To',
            items: [
              {
                label: 'Number',
                filter: callToNumber,
              },
              {
                label: 'City',
                filter: callToCities,
              },
              {
                label: 'State',
                filter: callToStates,
              },
              {
                label: 'Zip',
                filter: callToZips,
              },
              {
                label: 'Country',
                filter: callToCountries,
              },
            ],
          },
          {
            label: 'Closed Reason',
            filter: callClosedReasons,
          },
          {
            label: 'Disposition',
            filter: callDispositions,
          },
          {
            label: 'Origin',
            filter: callOrigins,
          },
          {
            label: 'Status',
            filter: callStatuses,
          },
          {
            label: 'Call Status',
            filter: callCallStatuses,
          },
          {
            label: 'STIR/Verstat',
            filter: callStirVerstat,
          },
          {
            label: 'TD Interaction ID',
            filter: callTdInteractionId,
          },
          {
            label: 'Twilio',
            items: [
              {
                label: 'API Version',
                filter: callTwilioApiVersion,
              },
              {
                label: 'Account SID',
                filter: callTwilioAccountSids,
              },
              {
                label: 'Callback Source',
                filter: callTwilioCallbackSources,
              },
            ],
          },
          {
            label: 'Comment',
            items: [
              {
                label: 'Author',
                filter: callCommentAuthors,
              },
              {
                label: 'Added By',
                filter: callCommentAddedByIds,
              },
              {
                label: 'Is System',
                filter: callCommentIsSystem,
              },
              {
                label: 'Is Autodialer',
                filter: callCommentIsAutodialer,
              },
              {
                label: 'External Message ID',
                filter: callCommentExternalMessageIds,
              },
              {
                label: 'Provider',
                filter: callCommentProviders,
              },
              {
                label: 'Ring Group',
                filter: callCommentRingGroups,
              },
            ],
          },
        ],
      },
      {
        label: 'UTM Parameters',
        items: [
          {
            label: 'UTM Source',
            filter: utmSource,
          },
          {
            label: 'UTM Medium',
            filter: utmMedium,
          },
          {
            label: 'UTM Contents',
            filter: utmContents,
          },
          {
            label: 'UTM Terms',
            filter: utmTerms,
          },
          {
            label: 'UTM Campaign',
            filter: utmCampaign,
          },
        ],
      }
    ];
  }

  syncFilterChipsToForm() {
    this.subs.sink = this.reportParamsForm.valueChanges.subscribe(() => {
      this.setFilterChips();
    });
  }

  setFilterChips() {

    const checkedCtrlNames: string[] = [];

    const updatedChips: typeof this.chips = [];

    for (const chip of this.chips) {

      checkedCtrlNames.push(chip.ctrlName);

      const value = this.reportParamsForm.get(chip.ctrlName).value;

      if (this.isDefault(chip.ctrlName, value)) { continue; }

      const formattedValue = this.formatValueForChips(chip.ctrlName, value);

      updatedChips.push({
        ...chip,
        value: formattedValue as (string | string[]),
      });
    }

    const ctrlNames: ReportFormControlName[] = [
      'jobStage',
      'jobState',
      'jobOrigins',
      'noOrigin',
      'jobSources',
      'jobSourceTiers',
      'jobFranchises',
      'jobAreas',
      'noSource',
      'jobTypes',
      'jobEstimateMethods',
      'jobEstimators',
      'jobSalesAgents',
      'attentionExplanations',
      'closedReason',
      'excludeClosedReasons',
      'noAttentionExplanation',
      'utmSources',
      'noUtmSource',
      'utmMedia',
      'noUtmMedium',
      'utmContents',
      'noUtmContent',
      'utmTerms',
      'noUtmTerm',
      'utmCampaigns',
      'noUtmCampaign',
      'jobHasCompletedEventOfType',
      'eventStatuses',
      'eventTypes',
      'eventSharedStatuses',
      'bookedOnDaysOfWeek',
      'bookedFromHour',
      'bookedUntilHour',
      'invoiceStatuses',
      'invoiceBalances',
      'transactionStages',
      'currency',
      'currencySource',
      'distanceMin',
      'distanceMax',
      'realizedBy',
      'excludeDamages',
      'excludeZeroTotalEvents',
      'countRevenueFromEventsWithStatus',
      'expenseIds',
      'taxIds',
      'callClosedReasons',
      'callModes',
      'callDispositions',
      'callFromNumber',
      'callToNumber',
      'callOrigins',
      'callStatuses',
      'callCallStatuses',
      'callToCities',
      'callToStates',
      'callToZips',
      'callToCountries',
      'callFromCities',
      'callFromStates',
      'callFromZips',
      'callFromCountries',
      'callStirVerstat',
      'callTdInteractionId',
      'callTwilioApiVersion',
      'callTwilioAccountSids',
      'callTwilioCallbackSources',
      'callCommentAuthors',
      'callCommentIsSystem',
      'callCommentAddedByIds',
      'callCommentIsAutodialer',
      'callCommentExternalMessageIds',
      'callCommentProviders',
      'callCommentRingGroups',
    ];

    for (const ctrlName of ctrlNames) {

      if (checkedCtrlNames.includes(ctrlName)) { continue; }

      const value = this.reportParamsForm.get(ctrlName).value;

      const formattedValue = this.formatValueForChips(ctrlName, value);

      if (!this.isDefault(ctrlName, value)) {
        updatedChips.push({
          ctrlName,
          name: this.controlDisplayNames[ctrlName],
          value: formattedValue as (string | string[]),
        });
      }
    }

    this.chips = updatedChips;
  }

  isDefault(controlName: ReportFormControlName, value: any) {

    const defaultValue = this.reportParamsFormDefault[controlName];

    if (Array.isArray(defaultValue)) {
      return Array.isArray(value) && (value.length === defaultValue.length && value.every((v) => (defaultValue as any[]).includes(v)));
    }

    if (typeof defaultValue === 'boolean') {
      return value === defaultValue;
    }

    if (typeof defaultValue === 'string') {
      return value === defaultValue;
    }

    return value === null || value === undefined || value === '';
  }

  resetFilter(ctrlName: ReportFormControlName) {
    this.reportParamsForm.get(ctrlName).setValue(this.reportParamsFormDefault[ctrlName], { emitEvent: false });
    this.updateParams();
    this.refreshReport();
  }

  downloadAsCsv() {
    this.actionsLoading = true;

    const currentQueryVars = this.getQueryVariables();

    this.previewReportGQL.fetch({
      ...currentQueryVars,
      linkFormat: 'sheets',
      skip: 0,
      limit: -1,
    }).subscribe((val) => {

      this.actionsLoading = false;

      const aggregationsCSV = unparse([
        val.data.previewReport.aggregationData.headers,
        ...val.data.previewReport.aggregationData.rows,
      ]);

      const rowsCSV = unparse([
        val.data.previewReport.data.headers,
        ...val.data.previewReport.data.rows,
      ]);

      const mergedCSVData = aggregationsCSV + '\n' + rowsCSV;

      const blob = new Blob([ mergedCSVData ], { type: 'text/csv' });

      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(blob);

      link.download = this.getFileName();

      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    });
  }

  async openExportDialog() {
    if (!this.existingReport) {
      this.localNotify.error('Please save the report before exporting');
      return;
    }
    this.actionsLoading = true;
    await this.refreshExportToken();
    this.actionsLoading = false;
    this.exportDialogOpen = true;
  }

  refreshExportToken() {
    this.exportTokenRefreshing = true;
    const obs = this.createDynamicReportExportTokenGQL.mutate({
      dynamicReportId: this.existingReport.id,
      performance: this.getExportTokenPerformance(),
    })
      .pipe(tap((res) => {
        this.exportTokenRefreshing = false;
        this.exportFormula = this.getFormula(res.data.createDynamicReportExportToken);
      }));
    return firstValueFrom(obs);
  }

  getExportTokenPerformance() {
    if (this.exportSettingsForm.value.includeSummary && this.exportSettingsForm.value.includeData) {
      return 'all';
    }
    if (this.exportSettingsForm.value.includeSummary) {
      return 'skip-data';
    }
    if (this.exportSettingsForm.value.includeData) {
      return 'skip-count';
    }
  }

  getFormula(token: Token) {
    const url = new URL(`${ location.protocol }//${ location.hostname }`);
    url.pathname = `/api/export/${ token.token }`;
    return `=IMPORTDATA("${ url.toString() }")`;
  }

  getFileName() {
    const [ start, end ] = this.reportParamsForm.value.customPeriod|| [];

    const formattedPeriod = periodToString(this.reportParamsForm.value.period, { start, end });

    const zone = this.brandingService.currentZone().value?.name || 'current zone';
    const time = dayjs().format('YYYY/MM/DD HH:mm');
    return `Revenue by ${this.groupBy} at ${time} [${formattedPeriod}] (${ zone }).csv`;
  }

  copyToClipboard() {
    this.actionsLoading = true;

    const currentQueryVars = this.getQueryVariables();

    this.dataOnlyGQL.fetch({
      ...currentQueryVars,
      linkFormat: 'sheets',
      skip: 0,
      limit: -1,
    }).subscribe((val) => {

      this.actionsLoading = val.loading;

      const rowsCSV = unparse([
        val.data.previewReport.data.headers,
        ...val.data.previewReport.data.rows,
      ], { delimiter: '\t' });

      this.clipboard.copy(rowsCSV);

      this.localNotify.success('Copied to clipboard');
    });
  }

  getDefaultColumns(groupBy: string) {

    const defaultColumns = [
      'subTotal',
      'discountedSubTotal',
      'discountTotal',
      'taxTotal',
      'total',
    ];

    if (!groupBy || groupBy === 'none') {
      return defaultColumns;
    }

    if (groupBy === 'jobCrewMember') {
      return ['job', 'crewMember', 'crewPayTotal', 'crewPayPaid', 'crewPayUnpaid'];
    }

    if (groupBy === 'eventCrewMember') {
      return [
        'job',
        'event',
        'eventBookingDateTime',
        'eventCompletedAt',
        'eventStatus',
        'crewMember',
        'crewPayTotal',
        'crewPayPaid',
        'crewPayUnpaid'
      ];
    }

    return [
      groupBy,
      ...defaultColumns,
    ];
  }

  syncFilterToQueryParams() {

    combineLatest([
      this.route.queryParams.pipe(filter((params) => 'zone' in params)),
      this.route.paramMap,
    ]).subscribe((val) => {

      const [ params, paramMap ] = val;

      const reportId = paramMap.get('reportId');

      if (reportId) {
        this.fetchExistingReport(reportId);
        return;
      }

      const { zone: _zone, ...filterParams } = params;

      this.queryParams.stripEmptyParams(filterParams);

      if (!Object.keys(filterParams).length) { return; }

      const paramTypes: ParamTypes = {
        startEnd: { params: [ 'customPeriod' ] },
        numArr: { params: [ 'bookedOnDaysOfWeek' ] },
        arr: { params: [
          'dataColumns',
          'aggregationColumns',
          'eventStatuses',
          'eventSharedStatuses',
          'eventTypes',
          'jobState',
          'jobStage',
          'jobSources',
          'jobFranchises',
          'jobAreas',
          'jobTypes',
          'jobEstimateMethods',
          'jobEstimators',
          'jobSalesAgents',
          'attentionExplanations',
          'excludeClosedReasons',
          'utmSources',
          'utmContents',
          'utmMedia',
          'utmTerms',
          'utmCampaigns',
          'jobOrigins',
          'jobHasCompletedEventOfType',
          'countRevenueFromEventsWithStatus',
          'transactionStages',
          'expenseIds',
          'taxIds',
          'callClosedReasons',
          'callModes',
          'callDispositions',
          'callOrigins',
          'callStatuses',
          'callCallStatuses',
          'callToCities',
          'callToStates',
          'callToZips',
          'callToCountries',
          'callFromCities',
          'callFromStates',
          'callFromZips',
          'callFromCountries',
          'callTwilioAccountSids',
          'callTwilioCallbackSources',
          'callCommentAuthors',
          'callCommentAddedByIds',
          'callCommentExternalMessageIds',
          'callCommentProviders',
          'callCommentRingGroups',
        ] },
        hour: { params: [ 'bookedFromHour', 'bookedUntilHour' ] },
        num: { params: [ 'distanceMin', 'distanceMax', 'limit' ] },
        unix: { params: [ 'realizedBy' ] },
        bool: { params: [
          'excludeDamages',
          'excludeZeroTotalEvents',
          'noUtmSource',
          'noUtmMedium',
          'noUtmContent',
          'noUtmTerm',
          'noUtmCampaign',
          'noSource',
          'noOrigin',
          'noAttentionExplanation',
          'callCommentIsSystem',
          'callCommentIsAutodialer',
        ] },
      }

      // Normalize date params from legacy format (MM-DD-YYYY) to canonical (YYYY-MM-DD)
      filterParams['customPeriod'] = normalizeDateRangeParam(filterParams['customPeriod']);

      // Note: We intentionally do NOT use timezone for parsing dates into the calendar.
      // The date string "2026-01-02" should display as January 2nd in the calendar regardless
      // of timezone. Timezone conversion is only applied when converting to Unix timestamps
      // for API calls (see getQueryVariables).
      const parsedValue = this.queryParams.parseQueryParams(filterParams, paramTypes);

      // Decode calculator formats from URL
      const columnFormats = this.queryParams.parseArrayQueryParam(filterParams['columnFormats']);

      if (columnFormats) {

        this.columnFormats.clear();

        for (const columnFormat of columnFormats) {

          const separatorIndex = columnFormat.indexOf(':');

          if (separatorIndex === -1) { continue; }

          const key = columnFormat.slice(0, separatorIndex);
          const format = columnFormat.slice(separatorIndex + 1);

          if (key && format !== undefined) {
            this.columnFormats.set(key, format);
          }
        }
      }

      const customColumnNames = this.queryParams.parseArrayQueryParam(filterParams['customColumnNames']);

      if (customColumnNames) {

        this.customColumnNames.clear();

        for (const entry of customColumnNames) {

          const separatorIndex = entry.indexOf(':');

          if (separatorIndex === -1) { continue; }

          const key = entry.slice(0, separatorIndex);
          const name = entry.slice(separatorIndex + 1);

          if (key && name) {
            this.customColumnNames.set(key, name);
          }
        }
      }

      // Use get() rather than contains(): contains() reports false for DISABLED
      // controls (e.g. customPeriod, which is disabled until the period is 'Custom'),
      // which would wrongly strip a validly-parsed value and fall back to today.
      for (const key of Object.keys(parsedValue)) {
        if (!this.reportParamsForm.get(key)) {
          delete parsedValue[key];
        }
      }

      if (!parsedValue.customPeriod) {
        const period = (parsedValue.period as AdvancedPeriodOptions) || this.defaultPeriod;
        parsedValue.customPeriod = this.periodService.getPeriodAsDateArray(period);
      }

      /**
       * In the url, we use 'any' to represent an empty array filter,
       * but here it's been parsed as an array with a single 'any' string.
       * We need to convert it back to an empty array.
       */
      for (const ctrlName of this.controlsWithDefaultFilters) {
        const val = parsedValue[ctrlName];
        if (Array.isArray(val) && val.includes('any')) {
          parsedValue[ctrlName] = [];
        }
      }

      parsedValue.dataColumns = parsedValue.dataColumns || this.getDefaultColumns(parsedValue.groupBy);

      const value = {
        ...this.reportParamsFormDefault,
        ...parsedValue,
      }

      this.reportParamsForm.setValue(value as any, { emitEvent: false });

      this.syncSourceTierNodesFromForm();

      this.checkUserRecords();

      if (value.period === 'Custom') {
        this.reportParamsForm.get('customPeriod').enable({ emitEvent: false });
      }

      if (value.groupBy) {
        this.setDataActions(value.groupBy);
      }

      this.clearInvalidHydratedSort(value.sort, value.dataColumns);

      this.setFilterChips();

      this.refreshReport();
    });

    this.subs.sink = this.reportParamsForm.valueChanges.subscribe((value) => {
      this.updateParams(value);
    });

  }

  updateParams(formValue?: typeof this.reportParamsForm.value) {

    if (!formValue) {
      formValue = this.reportParamsForm.value;
    }

    const isCustomPeriodInvalid = formValue.customPeriod && formValue.customPeriod.filter(Boolean).length < 2;

    if (isCustomPeriodInvalid) { return; }

    // Encode calculator column formats as array of "columnKey:format" strings
    const columnFormats = Array.from(this.columnFormats).map(
      ([key, format]) => `${key}:${format}`
    );

    // Same encoding as columnFormats; the dialog rejects ':' and ';' so the
    // join + ';'-delimited array param parses back unambiguously.
    const customColumnNames = Array.from(this.customColumnNames).map(
      ([key, name]) => `${key}:${name}`
    );

    const formattedValue = {
      ...formValue,
      bookedFromHour: this.formatTime(formValue.bookedFromHour) ? encodeURIComponent(this.formatTime(formValue.bookedFromHour)) : null,
      bookedUntilHour: this.formatTime(formValue.bookedUntilHour) ? encodeURIComponent(this.formatTime(formValue.bookedUntilHour)) : null,
      realizedBy: formValue.realizedBy ? dateToUnixSeconds(formValue.realizedBy) : null,
      columnFormats: columnFormats.length > 0 ? columnFormats : null,
      customColumnNames: customColumnNames.length > 0 ? customColumnNames : null,
    }

    const customControls = {
      dateRange: { controls: [ 'customPeriod' ] }
    };

    const timezone = this.brandingService.currentBranding.value?.timezone || environment.defaultTimezone;

    const queryParams = this.queryParams.formatSearchFormValues(formattedValue, customControls, { timezone, format: DATE_PARAM_FORMAT });

    this.setHasSavedChanges(true);

    if (this.existingReport) { return; }

    /**
     * If a control with a default filter was set to an empty array,* it will be stripped from the query params.
     * We need to add it back and set it to 'any' so the component knows to treat it as an empty filter.
     * Otherwise, it will apply the default filter.
     */
    for (const ctrlName of this.controlsWithDefaultFilters) {
      if (ctrlName in queryParams) { continue; }
      queryParams[ctrlName] = 'any';
    }

    const searchParams = new URLSearchParams(queryParams as any).toString();

    const newUrl = `${location.pathname}?${searchParams}`;

    history.replaceState(null, '', newUrl);
  }

  updateReport() {

    const queryVariables = this.getQueryVariables();

    const isCustomPeriod = this.reportParamsForm.value.period === 'Custom';

    this.saving = true;

    this.updateDyanmicReportGQL.mutate({
      reportId: this.existingReport.id,
      parameters: {
        start: isCustomPeriod ? queryVariables.start : undefined,
        end: isCustomPeriod ? queryVariables.end : undefined,
        groupBy: queryVariables.groupBy,
        period: this.reportParamsForm.value.period,
        periodType: queryVariables.periodType,
        filter: queryVariables.filter,
        dataColumns: queryVariables.dataColumns as string[],
        aggregationColumns: queryVariables.aggregationColumns as string[],
        sort: queryVariables.sort ?? null,
        columnParams: queryVariables.columnParams,
        columnFormats: queryVariables.columnFormats as ColumnFormat[],
        customColumnNames: queryVariables.customColumnNames as CustomColumnName[],
        currencySource: queryVariables.currencySource,
        limit: this.reportParamsForm.value.limit,
      }
    }).subscribe(() => {
      this.saving = false;
      this.localNotify.success('Report saved');
      this.setHasSavedChanges(false);
      // Requirement: Reload the report from the server after saving so the displayed
      // name, description and other metadata reflect what the backend persisted
      // (e.g. AI-regenerated description after filter changes).
      this.fetchExistingReport(this.existingReport.id, 'network-only');
    }, (err) => {
      this.saving = false;
      this.localNotify.apolloError('Failed to save report', err);
    });
  }

  deleteDynamicReport() {
    if (!this.existingReport) { return; }

    this.actionsLoading = true;

    this.deleteDynamicReportGQL.mutate({
      reportId: this.existingReport.id,
    }).subscribe(() => {
      this.actionsLoading = false;
      this.localNotify.success('Report deleted');
      this.router.navigate(['reports' ]);
    }, (err) => {
      this.actionsLoading = false;
      this.localNotify.apolloError('Failed to delete report', err);
    });
  }

  openCreateDynamicReportDialog() {

    const queryVariables = this.getQueryVariables();

    const isCustomPeriod = this.reportParamsForm.value.period === 'Custom';

    const input: DynamicReportParametersInput = {
      start: isCustomPeriod ? queryVariables.start : undefined,
      end: isCustomPeriod ? queryVariables.end : undefined,
      groupBy: queryVariables.groupBy,
      period: this.reportParamsForm.value.period,
      periodType: queryVariables.periodType,
      filter: queryVariables.filter,
      dataColumns: queryVariables.dataColumns as string[],
      aggregationColumns: queryVariables.aggregationColumns as string[],
      sort: queryVariables.sort,
      columnParams: queryVariables.columnParams,
      columnFormats: queryVariables.columnFormats as ColumnFormat[],
      customColumnNames: queryVariables.customColumnNames as CustomColumnName[],
      currencySource: queryVariables.currencySource,
      limit: this.reportParamsForm.value.limit,
    }

    // Requirement: Feature 2 (Issue #5821) — Pre-fill the folder path in the create
    // dialog using the `path` query param from the current URL, so saving from within
    // a folder context automatically places the new report in that folder.
    this.freyaMutate.openMutateObject({
      mutateType: 'create',
      objectType: 'dynamicReport',
      object: input,
      additionalValues: [
        {
          property: 'initialPath',
          value: this.route.snapshot.queryParams['path'] || '/',
        },
        {
          // Requirement: Supply existing path(s) so the folder autocomplete works.
          // From the preview page we only know the current report's path as a hint.
          property: 'existingPaths',
          value: ['/'].concat(
            this.existingReport?.path && this.existingReport.path !== '/'
              ? [this.existingReport.path]
              : []
          ),
        },
      ],
    });
  }

  openUpdateDynamicReportDialog() {
    if (!this.existingReport) { return; }

    const queryVariables = this.getQueryVariables();

    const isCustomPeriod = this.reportParamsForm.value.period === 'Custom';

    const input: DynamicReportParametersInput = {
      start: isCustomPeriod ? queryVariables.start : undefined,
      end: isCustomPeriod ? queryVariables.end : undefined,
      groupBy: queryVariables.groupBy,
      period: this.reportParamsForm.value.period,
      periodType: queryVariables.periodType,
      filter: queryVariables.filter,
      dataColumns: queryVariables.dataColumns as string[],
      aggregationColumns: queryVariables.aggregationColumns as string[],
      sort: queryVariables.sort,
      columnParams: queryVariables.columnParams,
      columnFormats: queryVariables.columnFormats as ColumnFormat[],
      customColumnNames: queryVariables.customColumnNames as CustomColumnName[],
      currencySource: queryVariables.currencySource,
      limit: this.reportParamsForm.value.limit,
    }

    this.freyaMutate.openMutateObject({
      mutateType: 'update',
      objectType: 'dynamicReport',
      object: input,
      additionalValues: [
        {
          property: 'dynamicReport',
          value: this.existingReport,
        },
      ],
    });
  }

  setBreadcrumb() {
    // Requirement: Breadcrumb on the dynamic report page should show:
    // home > Reports > [folder path segments] > report name
    // Each segment is a clickable link so the user can navigate back up the hierarchy.
    const items: MenuItem[] = [
      { label: 'Reports', routerLink: '/reports' },
    ];

    const rawPath = this.existingReport?.path;
    // Requirement: Decode the stored path before building routerLink arrays.
    // Backend-stored paths may contain URL-encoded characters (e.g. %20 for a space).
    // Passing encoded segments to Angular's router would double-encode them (%2520).
    let path = rawPath;
    if (path) {
      try { path = decodeURIComponent(path); } catch { /* keep as-is */ }
      try { path = decodeURIComponent(path); } catch { /* keep as-is */ }
    }
    if (path && path !== '/') {
      const segments = path.split('/').filter(Boolean);
      let built = '';
      for (const seg of segments) {
        built += '/' + seg;
        const routerParts = built.split('/').filter(Boolean);
        items.push({ label: seg, routerLink: ['/reports', ...routerParts] });
      }
    }

    items.push({ label: this.existingReport ? this.existingReport.name : 'New Report' });

    this.breadcrumb = items;
    this.home = { icon: 'pi pi-home', routerLink: '/home' };
  }

  fetchExistingReport(reportId: string, fetchPolicy: FetchPolicy = 'cache-first') {
    this.dynamicReportsGQL.fetch({
      skip: 0,
      limit: 1,
      filter: {
        reportIds: [ reportId ],
        zoneDir: ZoneDir.Any,
      },
    }, { fetchPolicy }).subscribe((res) => {

      const [ report ] = res.data.dynamicReports.reports;

      if (report) {
        this.loadExistingReport(report);
        return;
      }

      this.localNotify.error('Report not found');
      this.router.navigate(['reports', 'dynamic', 'new']);
    });
  }

  loadNewlyCreatedReports() {
    this.subs.sink = this.detailsHelper.getObjectUpdates('DynamicReport').subscribe((val) => {
      if (val.action === 'create') {
        this.router.navigate(['reports', 'dynamic', val.id]);
      } else if (val.action === 'update') {
        this.fetchExistingReport(val.id, 'network-only');
      }
    });
  }

  loadExistingReport(report: BaseDynamicReportFragment) {

    let customPeriod: Date[];

    if (report.start && report.end) {
      customPeriod = [new Date(report.start * 1000), new Date(report.end * 1000)];
    } else if (report.period) {
      customPeriod = this.periodService.getPeriodAsDateArray(report.period as AdvancedPeriodOptions);
    } else {
      customPeriod = this.periodService.getPeriodAsDateArray(this.defaultPeriod);
    };

    const filter: PreviewReportFilter = JSON.parse(report.filter);

    const params = {
      period: report.period as AdvancedPeriodOptions,
      periodType: report.periodType,
      groupBy: report.groupBy,
      dataColumns: report.dataColumns ?? [],
      aggregationColumns: report.aggregationColumns ?? [],
      sort: report.sort,
      jobStage: filter?.job?.getJobsOfStage || [],
      jobState: filter?.job?.getJobsOfState || [],
      jobSources: filter?.job?.sources || [],
      jobSourceTiers: filter?.job?.sourceTiers || [],
      noSource: filter?.job?.noSource || false,
      jobFranchises: filter?.job?.franchiseIds || [],
      jobAreas: filter?.job?.areaIds || [],
      jobOrigins: filter?.job?.origins || [],
      noOrigin: filter?.job?.noOrigin || false,
      jobTypes: filter?.job?.types || [],
      jobEstimateMethods: filter?.job?.estimateMethods || [],
      jobEstimators: filter?.job?.estimatorIds || [],
      jobSalesAgents: filter?.job?.salesAgentIds || [],
      attentionExplanations: filter?.job?.attentionExplanations || [],
      noAttentionExplanation: filter?.job?.noAttentionExplanation || false,
      closedReason: filter?.job?.closedReason || '',
      excludeClosedReasons: filter?.job?.excludeClosedReasons || [],
      jobHasCompletedEventOfType: filter?.job?.hasCompletedEventOfType || [],
      utmSources: filter?.job?.utmSources || [],
      noUtmSource: filter?.job?.noUtmSource || false,
      utmMedia: filter?.job?.utmMedia || [],
      noUtmMedium: filter?.job?.noUtmMedium || false,
      utmContents: filter?.job?.utmContents || [],
      noUtmContent: filter?.job?.noUtmContents || false,
      utmTerms: filter?.job?.utmTerms || [],
      noUtmTerm: filter?.job?.noUtmTerm || false,
      utmCampaigns: filter?.job?.utmCampaigns || [],
      noUtmCampaign: filter?.job?.noUtmCampaign || false,
      eventStatuses: filter?.event?.getEventsOfStatus || [],
      eventSharedStatuses: filter?.event?.getEventsOfSharedStatus || [],
      eventTypes: filter?.event?.getEventsOfType || [],
      bookedOnDaysOfWeek: filter?.event?.bookedOnDaysOfWeek || [],
      bookedFromHour: this.hourFilterToDate(filter?.event?.bookedFromHour),
      bookedUntilHour: this.hourFilterToDate(filter?.event?.bookedUntilHour),
      invoiceStatuses: filter?.invoice?.getInvoiceOfStatus || [],
      invoiceBalances: filter?.invoice?.balance || [],
      transactionStages: filter?.transaction?.stages || [],
      excludeDamages: filter?.charge?.excludeDamages || false,
      excludeZeroTotalEvents: filter?.event?.excludeZeroTotal || false,
      countRevenueFromEventsWithStatus: filter?.charge?.eventStatuses || [],
      customPeriod,
      currency: filter?.job?.currency || '',
      currencySource: report.currencySource ?? 'charge',
      distanceMin: filter?.event?.distanceMin || null,
      distanceMax: filter?.event?.distanceMax || null,
      realizedBy: null,
      expenseIds: filter?.expense?.ids || [],
      taxIds: filter?.charge?.taxIds || [],
      callClosedReasons: filter?.call?.closedReasons || [],
      callModes: filter?.call?.modes || [],
      callDispositions: filter?.call?.dispositions || [],
      callFromNumber: filter?.call?.fromNumber || '',
      callToNumber: filter?.call?.toNumber || '',
      callOrigins: filter?.call?.origins || [],
      callStatuses: filter?.call?.statuses || [],
      callCallStatuses: filter?.call?.callStatuses || [],
      callToCities: filter?.call?.toCities || [],
      callToStates: filter?.call?.toStates || [],
      callToZips: filter?.call?.toZips || [],
      callToCountries: filter?.call?.toCountries || [],
      callFromCities: filter?.call?.fromCities || [],
      callFromStates: filter?.call?.fromStates || [],
      callFromZips: filter?.call?.fromZips || [],
      callFromCountries: filter?.call?.fromCountries || [],
      callStirVerstat: filter?.call?.stirVerstat || '',
      callTdInteractionId: filter?.call?.tdInteractionId || '',
      callTwilioApiVersion: filter?.call?.twilioApiVersion || '',
      callTwilioAccountSids: filter?.call?.twilioAccountSids || [],
      callTwilioCallbackSources: filter?.call?.twilioCallbackSources || [],
      callCommentAuthors: filter?.callRootComment?.commentAuthors || [],
      callCommentIsSystem: filter?.callRootComment?.isSystem || false,
      callCommentAddedByIds: filter?.callRootComment?.addedByIds || [],
      callCommentIsAutodialer: filter?.callRootComment?.isAutodialer || false,
      callCommentExternalMessageIds: filter?.callRootComment?.externalMessageIds || [],
      callCommentProviders: filter?.callRootComment?.providers || [],
      callCommentRingGroups: filter?.callRootComment?.ringGroups || [],
      limit: report.limit ?? this.defaultLimit,
      periodBucket: report.columnParams?.periodBucket ?? 'month',
    }

    if (report.columnParams?.realizedBy) {
      params.realizedBy = report.columnParams.realizedBy;
    }

    const formats = new Map();

    for (const { column, format } of report.columnFormats || []) {
      formats.set(column, format);
    }

    this.columnFormats = formats;

    const customColumnNames = new Map<string, string>();

    for (const { column, name } of report.customColumnNames || []) {
      customColumnNames.set(column, name);
    }

    this.customColumnNames = customColumnNames;

    this.reportParamsForm.setValue(params, { emitEvent: false });

    this.clearInvalidHydratedSort(params.sort, params.dataColumns);

    this.syncSourceTierNodesFromForm();

    this.refreshEventTypeOptions();

    this.checkUserRecords();

    this.existingReport = report;

    this.setCustomPeriodEnabled(params.period);

    this.setFilterChips();

    this.setBreadcrumb();

    this.refreshReport();

    this.setDataActions(report.groupBy);

    this.setHasSavedChanges(false);
  }

  handleColReorder(event: TableColumnReorderEvent, columnType: 'dataColumns' | 'aggregationColumns') {

    const colCtrl = this.reportParamsForm.get(columnType);

    const columns = [ ...colCtrl.value ];

    // Destructure dragIndex and dropIndex from the event
    const { dragIndex, dropIndex } = event;

    // Ensure indices are within bounds
    if (dragIndex >= 0 && dragIndex < columns.length && dropIndex >= 0 && dropIndex < columns.length) {
      // Remove the column from the dragIndex
      const [movedColumn] = columns.splice(dragIndex, 1);
      // Add the column back at the dropIndex
      columns.splice(dropIndex, 0, movedColumn);
    }

    // Update the form control value with the reordered columns
    colCtrl.setValue(columns, { emitEvent: false });

    this.setHasSavedChanges(true);

    this.updateParams();

    this.previewReport();
  }

  formatValueForChips(ctrlName: ReportFormControlName, value: any) {

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (this.controlsWithDefaultFilters.includes(ctrlName) && !value.length) {
      return 'Any';
    }

    switch  (ctrlName) {
      case 'bookedOnDaysOfWeek':
        return value.map((dow: number) => this.formatDayOfTheWeek(dow));
      case 'bookedFromHour':
      case 'bookedUntilHour':
        return this.formatTime(value);
      case 'distanceMin':
      case 'distanceMax':
        return value ? `${value} m` : null;
      case 'realizedBy':
        return this.timezoneHelper.getDayJsTimeZoneObject(value).format('h:mm a, MMM DD YYYY');
      case 'jobEstimators':
      case 'jobFranchises':
        return value.map((franchiseId) => {
          const franchiseOption = this.franchiseOptions.find((o) => o.option === franchiseId);
          if (!franchiseOption) { return franchiseId };
          return franchiseOption.label;
        });
      case 'jobAreas':
        return value.map((areaId) => {
          const areaOption = this.areaOptions.find((o) => o.option === areaId);
          if (!areaOption) { return areaId };
          return areaOption.label;
        });
      case 'expenseIds':
        return value.map((expenseId) => {
          const expenseOption = this.expenseOptions.find((o) => o.option === expenseId);
          if (!expenseOption) { return expenseId };
          return expenseOption.label;
        })
      case 'taxIds':
        return value.map((taxId) => {
          const taxOption = this.taxOptions.find((o) => o.option === taxId);
          if (!taxOption) { return taxId };
          return taxOption.label;
        })
      case 'closedReason':
        return this.getClosedReasonTitle(value);
      case 'excludeClosedReasons':
        return (value as string[]).map((id) => this.getClosedReasonTitle(id));
      case 'jobSalesAgents':

        const userIds = value as string[];

        const hasAllNames = userIds.every((id) => this.userRecord[id]);

        if (!hasAllNames) {
          return 'Loading...';
        }

        return userIds.map((id) => {

          const user = this.userRecord[id];

          return `${ user.givenName } ${ user.familyName }`;

        }).join(', ');

      default:
        return value;
    }
  }

  /** Falls back to the id itself for a reason this zone's config does not list. */
  getClosedReasonTitle(id: string) {
    return this.closedReasons.find((reason) => reason.id === id)?.title ?? id;
  }

  formatTime(time: Date) {
    if (!time) { return null; }
    return dayjs(time).format('HH:mm');
  }

  formatDayOfTheWeek(dow: number) {
    const dayOfTheWeek = this.daysOfTheWeek.find((d) => d.option === dow);
    if (!dayOfTheWeek) {
      throw new Error(`Invalid day of the week ${dow}`);
    }
    return dayOfTheWeek.label;
  }

  hourFilterToDate(filter: HourFilter) {
    if (!filter) { return null; }
    return dayjs().hour(filter.hour).minute(filter.minute).toDate();
  }

  openCreateScheduleReportDialog() {
    if (!this.existingReport) {
      this.localNotify.error('Please save the report before exporting');
      return;
    }

    this.freyaMutate.openMutateObject({
      mutateType: 'create',
      objectType: 'ScheduledReport',
      additionalValues: [
        {
          property: 'dynamicReport',
          value: this.existingReport,
        },
      ],
    })

  }

  setDataActions(groupBy: string) {

    this.dataActions = [
      {
        label: 'Download as CSV',
        icon: 'pi pi-download',
        command: () => this.downloadAsCsv(),
      },
      {
        label: 'Semi-Live Export to Google Sheets',
        icon: 'pi pi-external-link',
        command: () => this.openExportDialog(),
        disabled: !this.existingReport,
      },
      {
        label: 'Add Calculator Column',
        icon: 'pi pi-calculator',
        command: () => this.openCalculatorDialog(),
        visible: groupBy === 'job',
      },
      {
        label: 'Add Tax Column',
        icon: 'pi pi-percentage',
        command: () => this.openTaxDialog(),
        visible: ['job', 'event', 'charge'].includes(groupBy),
      },
      {
        label: 'Custom Column Names',
        icon: 'pi pi-pencil',
        command: () => this.openCustomColumnNamesDialog(),
      },
      {
        label: 'Advanced Settings',
        icon: 'pi pi-cog',
        command: () => { this.advanceSettingsDialogOpen = true; },
      }
    ];
  }

  openCustomColumnNamesDialog() {
    const dialogValue: Record<string, string> = {};
    for (const column of this.reportParamsForm.get('dataColumns').value || []) {
      dialogValue[column] = this.customColumnNames.get(column) ?? '';
    }
    this.customColumnNamesDialogValue = dialogValue;
    this.customColumnNamesDialogOpen = true;
  }

  customColumnNameLabel(column: string): string {
    return this.dataColumnOptions?.find((opt) => opt.option === column)?.label ?? column;
  }

  isCustomColumnNameInvalid(value: string | undefined): boolean {
    if (!value) { return false; }
    return value.length > this.customColumnNameMaxLength
      || !this.customColumnNamePattern.test(value);
  }

  isCustomColumnNamesDialogInvalid(): boolean {
    return Object.values(this.customColumnNamesDialogValue).some(
      (value) => this.isCustomColumnNameInvalid(value),
    );
  }

  applyCustomColumnNames() {
    if (this.isCustomColumnNamesDialogInvalid()) { return; }

    const next = new Map<string, string>();
    for (const [column, rawValue] of Object.entries(this.customColumnNamesDialogValue)) {
      const value = (rawValue ?? '').trim();
      if (!value) { continue; }
      next.set(column, value);
    }

    this.customColumnNames = next;
    this.customColumnNamesDialogOpen = false;

    this.setHasSavedChanges(true);
    this.updateParams();
    this.previewReport();
  }

  startTimer() {
    this.subs.sink = this.reset$.pipe(
      switchMap(() => interval(1000))
    ).subscribe((lastRefreshSecs) => {
      this.setRefreshEnabled(lastRefreshSecs);
    });
  }

  restartTimer() {
    this.reset$.next();
  }

  setRefreshEnabled(lastRefreshSecs: number) {
    this.refreshEnabled = lastRefreshSecs > this.refreshEnabledMinSecs;
  }

  setHasSavedChanges(changes: boolean) {
    this.hasChanges = changes && Boolean(this.existingReport);
  }

  copyFormulaAndCreateNewSheet() {
    if (!this.exportFormula) {
      this.localNotify.error('No formula to copy');
      return;
    }
    this.clipboard.copy(this.exportFormula);
    window.open('https://docs.google.com/spreadsheets/create', '_blank');
  }

  atLeastOneTrue(group: FormGroup): ValidationErrors | null {
    const includeSummary = group.get('includeSummary')?.value;
    const includeData = group.get('includeData')?.value;

    if (includeSummary || includeData) {
      return null;
    }

    return { noSelection: true };
  }

  changeSortBy(sortBy: string) {
    this.reportParamsForm.controls.sort.setValue(sortBy);
  }

  resetSortOnColumnChanges() {

    this.subs.sink = this.reportParamsForm.valueChanges.subscribe(({ sort, dataColumns }) => {

      if (!sort) { return; }

      const [ key ] = sort.split(' ');

      if (dataColumns?.includes(key)) { return; }

      this.reportParamsForm.controls.sort.setValue(undefined);
    });
  }

  /**
   * resetSortOnColumnChanges() is a valueChanges subscriber, but the hydration
   * paths (URL sync + loadExistingReport) apply the form with { emitEvent: false },
   * so it never runs for hydrated params. Without this, a link / saved report whose
   * sort references a non-selected column would 400 on open (the backend rejects a
   * sort key that isn't a selected data column). Drop it here and tell the user,
   * rather than changing the report silently.
   */
  clearInvalidHydratedSort(sort: string | null | undefined, dataColumns: string[] | null | undefined) {
    const sortKey = sort?.split(' ')[0];
    if (sortKey && !dataColumns?.includes(sortKey)) {
      this.reportParamsForm.controls.sort.setValue(undefined, { emitEvent: false });
      this.localNotify.warning(
        'Sorting reset',
        `This report can't sort by "${ sortKey }" — it isn't one of the report's columns. Showing the default order.`,
      );
    }
  }

  setReorderIndicatorVisibility(visibility: 'visible' | 'hidden') {
    const reorderIndicatorTags = '.p-datatable-reorder-indicator-up, .p-datatable-reorder-indicator-down';
    const reorderIndicators = this.el.nativeElement.querySelectorAll(reorderIndicatorTags);
    for (const indicator of reorderIndicators) {
      this.renderer.setStyle(indicator, 'visibility', visibility);
    }
  }

  checkUserRecords() {

    if (this.userRecordChecked) { return; }

    const controlsWithUserIds: ReportFormControlName[] = [
      'jobEstimators',
      'jobSalesAgents',
    ];

    let userIds = [];

    for (const ctrlName of controlsWithUserIds) {
      userIds = userIds.concat(this.reportParamsForm.value[ctrlName] || []);
    }

    const usersMissing = userIds.some((id) => !this.userRecord[id]);

    if (usersMissing) {
      this.userPageUsersGQL.fetch({
        filter: { userIds, showDeactivated: true }
      }).subscribe((res) => {

        const record = {};

        for (const user of res?.data?.usersv2?.users || []) {
          record[user.id] = user;
        }

        this.userRecord = {
          ...this.userRecord,
          ...record,
        }
      });
    }

    this.userRecordChecked = true;
  }

  searchExpenses(input: string) {
    this._expensesQuery.next(input);
  }

  openCalculatorDialog() {
    const currentColumns = this.reportParamsForm.get('dataColumns').value || [];

    const columnKeyCtrl = this.calculatorColumnForm.get('columnKey');

    columnKeyCtrl.setValidators([
      Validators.required,
      Validators.pattern(/^[^\s]+$/),  // No spaces allowed
      this.createDuplicateKeyValidator(currentColumns)
    ]);

    columnKeyCtrl.updateValueAndValidity();

    this.calculatorDialogOpen = true;
  }

  addCalculatorColumn() {
    if (this.calculatorColumnForm.invalid) {
      return;
    }

    const { columnKey, format } = this.calculatorColumnForm.value;

    const prefixedKey = `calculator_${columnKey}`;

    // Store format
    if (typeof format === 'string' && format.trim().length > 0) {
      this.columnFormats.set(prefixedKey, format);
    }

    // Add column to dataColumns
    const dataColumnsCtrl = this.reportParamsForm.get('dataColumns');

    const currentColumns = dataColumnsCtrl.value || [];

    dataColumnsCtrl.setValue([ ...currentColumns, prefixedKey ]);

    this.calculatorColumnForm.reset();
    this.calculatorDialogOpen = false;
  }

  openTaxDialog() {
    const currentColumns = this.reportParamsForm.get('dataColumns').value || [];

    const taxTypeCtrl = this.taxColumnForm.get('taxType');

    taxTypeCtrl.setValidators([
      Validators.required,
      this.createDuplicateTaxValidator(currentColumns)
    ]);

    taxTypeCtrl.updateValueAndValidity();

    this.taxColumnDialogOpen = true;
  }

  addTaxColumn() {
    if (this.taxColumnForm.invalid) {
      return;
    }

    const { taxType } = this.taxColumnForm.value;

    // Replace spaces with underscores for column key
    const prefixedKey = `taxTotal_${taxType.replace(/\s+/g, '_')}`;

    // Add column to dataColumns
    const dataColumnsCtrl = this.reportParamsForm.get('dataColumns');

    const currentColumns = dataColumnsCtrl.value || [];

    dataColumnsCtrl.setValue([ ...currentColumns, prefixedKey ]);

    this.taxColumnForm.reset();
    this.taxColumnDialogOpen = false;
  }

  setDataColumnOptions(staticColumnOptions: { option: string; label: string }[]) {

    const columnOptions = [
      ...staticColumnOptions,
    ];

    for (const column of this.reportParamsForm.get('dataColumns').value) {

      // Handle calculator columns
      if (column.startsWith('calculator_')) {
        const key = column.slice('calculator_'.length);
        const uncammeled = key.replace(/([A-Z])/g, ' $1');
        columnOptions.push({
          option: column,
          label: strToTitleCase(uncammeled),
        });
      }
      // Handle dynamic tax columns (but not static ones already in options)
      else if (column.startsWith('taxTotal_') && !staticColumnOptions.some(opt => opt.option === column)) {
        const taxType = column.slice('taxTotal_'.length).replace(/_/g, ' ');
        columnOptions.push({
          option: column,
          label: `${taxType} Total`,
        });
      }
    }

    this.dataColumnOptions = columnOptions;
  }
}
